import { CollectionName, collectionAlias } from "@tc/search-document-builders"
import {
  UNIT_LIST_CODE,
  VOLUME_LIST_CODE,
  WEIGHT_LIST_CODE,
} from "../constants"
import { toPhytoPdfPath } from "../utils/helper"
import {
  FieldDefinition,
  FieldUniqueIdentifier,
  IFieldRegistry,
} from "../utils/types"
import * as Fuid from "./fuid"

/**
 * Maps collection aliases to APeCert list_name values for Get Template XML response.
 * Only includes collections that are value_lists (have predefined options).
 *
 * Per ADO comment #7889908:
 * - countries → "Country"
 * - process → "ProcessType"
 * - phyto-botanical-name → "Botanical Name"
 * - agency, port, registrations → NOT value_lists (use ID only)
 *   - Port is captured in mandatory items and submission XML section as ID
 *
 * Note: Unit collection is NOT included here because it has multiple list_codes
 * (unitWeight, unitVolume, unitDuration, unitTemperature) depending on the FUID.
 * Use buildUnitFuidToListCodeMap() to get the FUID → list_code mapping dynamically.
 *
 * @see AC4 of Story #181912
 */
export const COLLECTION_TO_LIST_NAME: Record<string, string> = {
  [collectionAlias(CollectionName.Countries)]: "Country",
  [collectionAlias(CollectionName.Process)]: "ProcessType",
  [collectionAlias(CollectionName.PhytoBotanicalName)]: "Botanical Name",
}

/**
 * @fileoverview Defines the central registry mapping FUIDs to their service-specific representations.
 * This registry is the core lookup table used by the data conversion functions.
 *
 * --- Structure ---
 * - It exports `defaultFieldRegistry`, an instance of `Map` implementing `IFieldRegistry`.
 * - **Keys:** `FieldUniqueIdentifier` constants imported from `./fuid`.
 * - **Values:** `FieldDefinition` objects containing:
 *   - `fuid`: The FUID itself.
 *   - `description`: (Optional) Human-readable description.
 *   - `serviceRepresentations`: An object mapping `ServiceName` (e.g., 'CORE', 'DFE')
 *     to a `ServiceFieldRepresentation` ({ path: '...' }).
 *
 * --- Path Notation ---
 * - The `path` string within `ServiceFieldRepresentation` indicates the data location.
 * - Use dot notation for objects (e.g., `user.address.city`).
 * - Use wildcard `[*]` for properties within array items (e.g., `items[*].id`).
 *   The converter iterates arrays based on this wildcard.
 *
 * --- How to Add/Update Mappings ---
 * 1. Ensure the FUID is defined in `./fuid.ts`.
 * 2. Import the FUID constant.
 * 3. Add or update the entry in the `defaultFieldRegistry` Map.
 * 4. **Crucially:** Define the correct `path` for *every* relevant service under `serviceRepresentations`. Check actual data structures for accuracy.
 */

export const defaultFieldRegistry: IFieldRegistry = new Map<
  FieldUniqueIdentifier,
  FieldDefinition
>([
  [
    Fuid.REQUEST_ID,
    {
      fuid: Fuid.REQUEST_ID,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "id" },
        CORE_TRANSFER_RESPONSE: { path: "id" },
        DFE: { path: "id" },
        B2G_SUBMISSION_PHYTO_XML: { path: "id" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "id" },
        G2G_UNCEFACT_XML: { path: "id" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "id" },
      },
    },
  ],
  [
    Fuid.TEMPLATE_CODE,
    {
      fuid: Fuid.TEMPLATE_CODE,
      serviceRepresentations: {
        CORE_TRANSFER_RESPONSE: { path: "template.code" },
        CORE_EXPORT_RESPONSE: { path: "template.code" },
        B2G: { path: "templateCode" },
        DFE: { path: "templateCode" },
        B2G_SUBMISSION_PHYTO_XML: { path: "template.id" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Identifiers.CertType" },
        ITEXT: { path: "TemplateId" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "template.code" },
        G2G_UNCEFACT_XML: { path: "template.code" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "template.code" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Identifiers/TemplateID",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Identifiers/CertType",
        },
      },
    },
  ],
  [
    Fuid.SEQUENCE_NUMBER,
    {
      fuid: Fuid.SEQUENCE_NUMBER,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "sequenceNumber" },
        CORE_TRANSFER_RESPONSE: { path: "certificateNumber" },
        CORE_EXPORT_INPUT: { path: "sequenceNumber" },
        CORE_EXPORT_RESPONSE: { path: "certificateNumber" },
        B2G: { path: "sequenceNumber" },
        DFE: { path: "certificateNumber" },
        G2G_IPPC_PHYTO_XML: { path: "certificateNumber" },
        G2G_UNCEFACT_XML: { path: "certificateNumber" },
        B2G_SUBMISSION_PHYTO_XML: { path: "certificateNumber" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Identifiers.CertificateID" },
        ITEXT: { path: "ExternalIdentifier" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "certificateNumber" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "certificateNumber" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Identifiers/CertificateID",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_COMMODITY,
    {
      fuid: Fuid.REQUEST_COMMODITY,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "commodity" },
        CORE_PHYTO_RESPONSE: { path: "commodity.id" },
        CORE_TRANSFER_INPUT: { path: "commodity" },
        CORE_TRANSFER_RESPONSE: { path: "commodity.id" },
        B2G: { path: "commodity" },
        DFE: { path: "commodityId" },
        ITEXT: { path: "CommodityCode" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "commodity.id" },
        G2G_UNCEFACT_XML: { path: "commodity.id" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "commodity.id" },
      },
    },
  ],
  [
    Fuid.REQUEST_DESTINATION_COUNTRY,
    {
      fuid: Fuid.REQUEST_DESTINATION_COUNTRY,
      description: "Destination Country code",
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "destinationCountry" },
        CORE_PHYTO_RESPONSE: { path: "destinationCountry" },
        CORE_TRANSFER_INPUT: { path: "destinationCountry" },
        CORE_TRANSFER_RESPONSE: { path: "destinationCountry.id" },
        CORE_EXPORT_INPUT: { path: "destinationCountry" },
        CORE_EXPORT_RESPONSE: { path: "destinationCountry" },
        B2G: { path: "destinationCountry" },
        DFE: { path: "destinationCountryId" },
        G2G_IPPC_PHYTO_XML: { path: "destinationCountry" },
        G2G_UNCEFACT_XML: { path: "destinationCountry" },
        B2G_SUBMISSION_PHYTO_XML: { path: "destinationCountry" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "DestinationCountryID" },
        PHYTO_PDF: {
          path: `${toPhytoPdfPath(Fuid.REQUEST_DESTINATION_COUNTRY)}`,
        },
        ITEXT: { path: "ImportingCountry" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "destinationCountry.id" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "destinationCountry.id" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/ImportingCountryID",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/DestinationCountryID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
      },
    },
  ],
  [
    Fuid.PARTY_CONSIGNOR_ID,
    {
      fuid: Fuid.PARTY_CONSIGNOR_ID,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "consignor" },
        CORE_PHYTO_RESPONSE: { path: "consignor" },
        CORE_TRANSFER_INPUT: { path: "consignor" },
        CORE_TRANSFER_RESPONSE: { path: "consignor" },
        CORE_EXPORT_INPUT: { path: "consignor" },
        CORE_EXPORT_RESPONSE: { path: "consignor" },
        B2G: { path: "consignor" },
        DFE: { path: "consignorId" },
        G2G_IPPC_PHYTO_XML: { path: "consignor.id" },
        G2G_UNCEFACT_XML: { path: "consignor.id" },
        B2G_SUBMISSION_PHYTO_XML: { path: "consignor.id" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Parties.OrganisationID" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.PARTY_CONSIGNOR_ID) },
        ITEXT: { path: "ExporterIdentifier" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "consignor.id" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/ExporterID",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/OrganisationID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Registrations),
          usePermissions: true,
          collectionSettings: {
            queryAttributes:
              "registration_name,organisation_name,address.physical_address,address.physical_city,address.physical_city_postcode,address.physical_country_code,address.physical_state",
            valueAttribute: "registration_name",
            displayAttr: "organisation_name",
            orderBy: "registration_name:asc",
            filter_by:
              "registration_type_code:= [RMP, EX, DEX, GFP] && status:='ACTIVE'",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Registrations),
          usePermissions: true,
          collectionSettings: {
            queryAttributes:
              "registration_name,organisation_name,type_code,physical_address,physical_city,physical_city_postcode",
            valueAttribute: "registration_name",
            displayAttr: "organisation_name",
            orderBy: "organisation_name:asc",
            filter_by:
              "registration_type_code:= [RMP, EX, IPO, DEX, RCS, HS, SMO, WL, BPP, GT, AFP, AP, GFP, OPS, EAP] && status:='ACTIVE'",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Registrations),
          usePermissions: true,
          collectionSettings: {
            queryAttributes:
              "registration_name,organisation_name,address.physical_address,address.physical_city,address.physical_city_postcode,address.physical_country_code,address.physical_state",
            valueAttribute: "registration_name",
            displayAttr: "organisation_name",
            orderBy: "organisation_name:asc",
            filter_by: "registration_type_code:='PHY' && status:='ACTIVE'",
          },
        },
      },
    },
  ],
  [
    Fuid.PARTY_VERIFYING_AGENCY,
    {
      fuid: Fuid.PARTY_VERIFYING_AGENCY,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: { path: "verifyingAgencyId" },
        CORE_EXPORT_RESPONSE: { path: "verifyingAgencyId" },
        CORE_PHYTO_INPUT: { path: "verifyingAgencyId" },
        CORE_PHYTO_RESPONSE: { path: "verifyingAgencyId" },
        DFE: { path: "verifyingAgencyId" },
        B2G_SUBMISSION_PHYTO_XML: { path: "verifyingAgencyId" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Identifiers.IVAID" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.PARTY_VERIFYING_AGENCY) },
        ITEXT: { path: "InspectionAgency" },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Identifiers/IVAID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Agency),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "display_name,agency_type,identifier",
            valueAttribute: "id",
            displayAttr: "display_name",
            orderBy: "display_name:asc",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Agency),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "display_name,agency_type,identifier",
            valueAttribute: "id",
            displayAttr: "display_name",
            orderBy: "display_name:asc",
            filter_by: "status:='ACTIVE'",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Agency),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "display_name,agency_type,identifier",
            valueAttribute: "id",
            displayAttr: "display_name",
            orderBy: "display_name:asc",
            filter_by: "status:='ACTIVE'",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: false,
        AP_TRANSFER: false,
        PHYTO_EXPORT: false,
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_ORG_CERT_REF_CODE,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_ORG_CERT_REF_CODE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "attributes.orgCertRefCode" },
        CORE_PHYTO_RESPONSE: { path: "attributes.orgCertRefCode" },
        DFE: { path: "orgCertRefCode" },
        B2G_SUBMISSION_PHYTO_XML: { path: "attributes.orgCertRefCode" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Identifiers.OrgCertRefCode",
        },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.REQUEST_ATTRIBUTE_ORG_CERT_REF_CODE),
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Identifiers/OrgCertRefCode",
        },
      },
    },
  ],
  [
    Fuid.PHYTO_SCHEME_CONTROL,
    {
      fuid: Fuid.PHYTO_SCHEME_CONTROL,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "attributes.phytoSchemeControl" },
        CORE_PHYTO_RESPONSE: { path: "attributes.phytoSchemeControl" },
        DFE: { path: "phytoSchemeControl" },
        B2G_SUBMISSION_PHYTO_XML: { path: "attributes.phytoSchemeControl" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "SchemeControl" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.PHYTO_SCHEME_CONTROL) },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/SchemeControl",
        },
      },
    },
  ],
  [
    Fuid.PHYTO_STATEMENT_OF_RELABELLING,
    {
      fuid: Fuid.PHYTO_STATEMENT_OF_RELABELLING,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "attributes.phytoStatementOfRelabelling" },
        CORE_PHYTO_RESPONSE: { path: "attributes.phytoStatementOfRelabelling" },
        DFE: { path: "phytoStatementOfRelabelling" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "attributes.phytoStatementOfRelabelling",
        },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.PHYTO_STATEMENT_OF_RELABELLING),
        },
      },
    },
  ],
  [
    Fuid.PHYTO_ATTRIBUTE_CERT_SUB_TYPE,
    {
      fuid: Fuid.PHYTO_ATTRIBUTE_CERT_SUB_TYPE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "attributes.phytoCertSubType" },
        CORE_PHYTO_RESPONSE: { path: "attributes.phytoCertSubType" },
        DFE: { path: "phytoCertSubType" },
        B2G_SUBMISSION_PHYTO_XML: { path: "attributes.phytoCertSubType" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Identifiers.CertSubType" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.PHYTO_ATTRIBUTE_CERT_SUB_TYPE) },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Identifiers/CertSubType",
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_PHYTO_CERTIFICATE_NUMBER,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_PHYTO_CERTIFICATE_NUMBER,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "attributes.phytoCertificateNumber" },
        CORE_PHYTO_RESPONSE: { path: "attributes.phytoCertificateNumber" },
        DFE: { path: "phytoCertificateNumber" },
      },
    },
  ],
  [
    Fuid.REQUEST_IMPORT_PERMIT_NUMBERS,
    {
      fuid: Fuid.REQUEST_IMPORT_PERMIT_NUMBERS,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "attributes.importPermitNumbers",
        },
        CORE_PHYTO_RESPONSE: {
          path: "attributes.importPermitNumbers",
        },
        DFE: { path: "importPermitNumbers" },
        G2G_IPPC_PHYTO_XML: { path: "attributes.importPermitNumbers" },
        G2G_UNCEFACT_XML: { path: "attributes.importPermitNumbers" },
        B2G_SUBMISSION_PHYTO_XML: { path: "attributes.importPermitNumbers" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "AdditionalDeclarations.ImportPermits[*]",
        },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.REQUEST_IMPORT_PERMIT_NUMBERS) },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/AdditionalDeclarations/ImportPermitNumbers/ImportPermit",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTR_LETTER_OF_CREDIT_REF_CODE,
    {
      fuid: Fuid.REQUEST_ATTR_LETTER_OF_CREDIT_REF_CODE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "attributes.letterOfCreditRefCode",
        },
        CORE_PHYTO_RESPONSE: {
          path: "attributes.letterOfCreditRefCode",
        },
        DFE: { path: "letterOfCreditRefCode" },
        B2G_SUBMISSION_PHYTO_XML: { path: "attributes.letterOfCreditRefCode" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "UnverifiedInformation.LetterOfCreditRefCode",
        },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.REQUEST_ATTR_LETTER_OF_CREDIT_REF_CODE),
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/UnverifiedInformation/LetterOfCreditRefCode",
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_ATTR_PHYTO_LOT_REFERENCE_NUMBER,
    {
      fuid: Fuid.REQUEST_ATTR_PHYTO_LOT_REFERENCE_NUMBER,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "attributes.phytoLotRefNumber",
        },
        CORE_PHYTO_RESPONSE: {
          path: "attributes.phytoLotRefNumber",
        },
        CORE_TRANSFER_INPUT: { path: "attributes.lot" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.lot" },
        CORE_EXPORT_INPUT: { path: "attributes.lot" },
        CORE_EXPORT_RESPONSE: { path: "attributes.lot" },
        B2G: { path: "attributes.lot" },
        DFE: { path: "lot" },
        G2G_UNCEFACT_XML: { path: "attributes.lot" },
        B2G_SUBMISSION_PHYTO_XML: { path: "attributes.phytoLotRefNumber" },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.REQUEST_ATTR_PHYTO_LOT_REFERENCE_NUMBER),
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Identifiers.OfficialLotRefNumber",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Identifiers/OfficialLotRefNumber",
        },
        ITEXT: { path: "Lot" },
        ITEXT_INPUT: { path: "attributes.lot.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.lot" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.lot" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTR_PHYTO_PACKHOUSE,
    {
      fuid: Fuid.REQUEST_ATTR_PHYTO_PACKHOUSE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "attributes.phytoPackhouse",
        },
        CORE_PHYTO_RESPONSE: {
          path: "attributes.phytoPackhouse",
        },
        DFE: { path: "phytoPackhouse" },
      },
    },
  ],
  [
    Fuid.REQUEST_AUTO_APPROVAL,
    {
      fuid: Fuid.REQUEST_AUTO_APPROVAL,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "eligibility_declaration" },
        CORE_TRANSFER_RESPONSE: { path: "eligibility_declaration" },
        B2G: { path: "eligibility_declaration" },
        DFE: { path: "autoApproval" },
        ITEXT: { path: "AutoApproval" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "eligibility_declaration" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "eligibility_declaration" },
        SUBMISSION_PREVIEW_AP_XML_PATH: { path: "/Certificate/AutoApproval" },
      },
    },
  ],
  [
    Fuid.REQUEST_CONSIGNEE,
    {
      fuid: Fuid.REQUEST_CONSIGNEE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "consignee" },
        CORE_TRANSFER_RESPONSE: { path: "consignee" },
        B2G: { path: "consignee" },
        DFE: { path: "consigneeId" },
        ITEXT: { path: "ConsigneeIdentifier" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "consignee.id" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "importer.id" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/ConsigneeID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Registrations),
          usePermissions: false,
          collectionSettings: {
            queryAttributes:
              "registration_name,organisation_name,type_code,physical_address,physical_city,physical_city_postcode",
            valueAttribute: "registration_name",
            displayAttr: "organisation_name",
            orderBy: "registration_name:asc",
            filter_by:
              "registration_type_code:= [RMP, EX, DEX, GFP] && status:='ACTIVE'",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Registrations),
          usePermissions: false,
          collectionSettings: {
            queryAttributes:
              "registration_name,organisation_name,type_code,physical_address,physical_city,physical_city_postcode",
            valueAttribute: "registration_name",
            displayAttr: "organisation_name",
            orderBy: "registration_name:asc",
            filter_by:
              "registration_type_code:= [RMP, EX, IPO, DEX, RCS, HS, SMO, WL, BPP, GT, AFP, AP, GFP, OPS, EAP] && status:='ACTIVE'",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_URGENCY,
    {
      fuid: Fuid.REQUEST_URGENCY,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "urgency" },
        CORE_TRANSFER_RESPONSE: { path: "urgency" },
        CORE_EXPORT_INPUT: { path: "urgency" },
        CORE_EXPORT_RESPONSE: { path: "urgency" },
        B2G: { path: "urgency" },
        DFE: { path: "urgency" },
        B2G_SUBMISSION_PHYTO_XML: { path: "urgency" },
        ITEXT: { path: "Urgency" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "urgency" },
        G2G_UNCEFACT_XML: { path: "urgency" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "urgency" },
        SUBMISSION_PREVIEW_AP_XML_PATH: { path: "/Certificate/Urgency" },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=Urgency && active:=true`,
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=Urgency && active:=true`,
          },
        },
      },
    },
  ],
  [
    Fuid.REQUEST_TRANSPORT_MODE_CODE,
    {
      fuid: Fuid.REQUEST_TRANSPORT_MODE_CODE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transport.transportMode" },
        CORE_TRANSFER_RESPONSE: { path: "transport.transportMode" },
        CORE_EXPORT_INPUT: { path: "transport.transportMode" },
        CORE_EXPORT_RESPONSE: { path: "transport.transportMode" },
        CORE_PHYTO_INPUT: { path: "transport.transportMode" },
        CORE_PHYTO_RESPONSE: { path: "transport.transportMode" },
        B2G: { path: "transport.transportMode" },
        DFE: { path: "transportMode" },
        G2G_IPPC_PHYTO_XML: { path: "transport.transportMode" },
        G2G_UNCEFACT_XML: { path: "transport.transportMode" },
        B2G_SUBMISSION_PHYTO_XML: { path: "transport.transportMode" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Transport.MeansOfConveyance",
        },
        PHYTO_PDF: {
          path: `${toPhytoPdfPath(Fuid.REQUEST_TRANSPORT_MODE_CODE)}`,
        },
        ITEXT: { path: "TransportMode" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "transport.transportMode" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "transport.transportMode" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Transport/TransportMode",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Transport/MeansOfConveyance",
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_CARRIER_NAME,
    {
      fuid: Fuid.REQUEST_CARRIER_NAME,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transport.carrierName" },
        CORE_TRANSFER_RESPONSE: { path: "transport.carrierName" },
        CORE_EXPORT_INPUT: { path: "transport.carrierName" },
        CORE_EXPORT_RESPONSE: { path: "transport.carrierName" },
        CORE_PHYTO_INPUT: { path: "transport.carrierName" },
        CORE_PHYTO_RESPONSE: { path: "transport.carrierName" },
        B2G: { path: "transport.carrierName" },
        DFE: { path: "carrierName" },
        G2G_IPPC_PHYTO_XML: { path: "transport.carrierName" },
        G2G_UNCEFACT_XML: { path: "transport.carrierName" },
        B2G_SUBMISSION_PHYTO_XML: { path: "transport.carrierName" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Transport.Vessel" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.REQUEST_CARRIER_NAME) },
        ITEXT: { path: "CarrierName" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "transport.carrierName" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "transport.carrierName" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Transport/CarrierName",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Transport/Vessel",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_CONVEYANCE_REFERENCE,
    {
      fuid: Fuid.REQUEST_CONVEYANCE_REFERENCE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transport.conveyanceReference" },
        CORE_TRANSFER_RESPONSE: { path: "transport.conveyanceReference" },
        CORE_EXPORT_INPUT: { path: "transport.conveyanceReference" },
        CORE_EXPORT_RESPONSE: { path: "transport.conveyanceReference" },
        CORE_PHYTO_INPUT: { path: "transport.conveyanceReference" },
        CORE_PHYTO_RESPONSE: { path: "transport.conveyanceReference" },
        B2G: { path: "transport.conveyanceReference" },
        DFE: { path: "conveyanceReference" },
        G2G_IPPC_PHYTO_XML: { path: "transport.conveyanceReference" },
        G2G_UNCEFACT_XML: { path: "transport.conveyanceReference" },
        B2G_SUBMISSION_PHYTO_XML: { path: "transport.conveyanceReference" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Transport.Voyage" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.REQUEST_CONVEYANCE_REFERENCE) },
        ITEXT: { path: "ConveyanceReference" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "transport.conveyanceReference",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "transport.conveyanceReference",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Transport/ConveyanceReference",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Transport/Voyage",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_TRANSPORT_LOCAL_CARRIER,
    {
      fuid: Fuid.REQUEST_TRANSPORT_LOCAL_CARRIER,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transport.localCarrier" },
        CORE_TRANSFER_RESPONSE: { path: "transport.localCarrier" },
        CORE_EXPORT_INPUT: { path: "transport.localCarrier" },
        CORE_EXPORT_RESPONSE: { path: "transport.localCarrier" },
        B2G: { path: "transport.localCarrier" },
        DFE: { path: "localCarrier" },
        ITEXT: { path: "LocalCarrier" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "transport.localCarrier" },
        G2G_UNCEFACT_XML: { path: "transport.localCarrier" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "transport.localCarrier" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Transport/LocalCarrier",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_TRANSPORT_LOCAL_CARRIER_REFERENCE,
    {
      fuid: Fuid.REQUEST_TRANSPORT_LOCAL_CARRIER_REFERENCE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transport.localCarrierReference" },
        CORE_TRANSFER_RESPONSE: { path: "transport.localCarrierReference" },
        CORE_EXPORT_INPUT: { path: "transport.localCarrierReference" },
        CORE_EXPORT_RESPONSE: { path: "transport.localCarrierReference" },
        B2G: { path: "transport.localCarrierReference" },
        DFE: { path: "localCarrierReference" },
        ITEXT: { path: "LocalCarrierReference" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "transport.localCarrierReference",
        },
        G2G_UNCEFACT_XML: {
          path: "transport.localCarrierReference",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "transport.localCarrierReference",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Transport/LocalCarrierReference",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_LOADING_PORT,
    {
      fuid: Fuid.REQUEST_LOADING_PORT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transport.loadingPort" },
        CORE_TRANSFER_RESPONSE: { path: "transport.loadingPort" },
        CORE_EXPORT_INPUT: { path: "transport.loadingPort" },
        CORE_EXPORT_RESPONSE: { path: "transport.loadingPort" },
        CORE_PHYTO_INPUT: { path: "transport.loadingPort" },
        CORE_PHYTO_RESPONSE: { path: "transport.loadingPort" },
        B2G: { path: "transport.loadingPort" },
        DFE: { path: "loadingPort" },
        G2G_IPPC_PHYTO_XML: { path: "transport.loadingPort" },
        G2G_UNCEFACT_XML: { path: "transport.loadingPort" },
        B2G_SUBMISSION_PHYTO_XML: { path: "transport.loadingPort" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Transport.LoadingPortID" },
        PHYTO_PDF: {
          path: `${toPhytoPdfPath(Fuid.REQUEST_LOADING_PORT)}`,
        },
        ITEXT: { path: "LoadingPort" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "transport.loadingPort" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "transport.loadingPort" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Transport/Ports/LoadingPortID",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Transport/LoadingPortID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Port),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "port_name,port_code",
            valueAttribute: "port_code",
            displayAttr: "port_code,port_name",
            orderBy: "port_name:asc",
            filter_by: "country_code:='NZ' && active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Port),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "port_name,port_code",
            valueAttribute: "port_code",
            displayAttr: "port_code,port_name",
            orderBy: "port_name:asc",
            filter_by: "country_code:='NZ' && active:=true",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Port),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "port_name,port_code",
            valueAttribute: "port_code",
            displayAttr: "port_code,port_name",
            orderBy: "port_name:asc",
            filter_by: "country_code:='NZ' && active:=true",
          },
        },
      },
    },
  ],
  [
    Fuid.REQUEST_FINAL_DESTINATION,
    {
      fuid: Fuid.REQUEST_FINAL_DESTINATION,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transport.finalDestination" },
        CORE_TRANSFER_RESPONSE: { path: "transport.finalDestination" },
        CORE_EXPORT_INPUT: { path: "transport.finalDestination" },
        CORE_EXPORT_RESPONSE: { path: "transport.finalDestination" },
        B2G: { path: "transport.finalDestination" },
        DFE: { path: "finalDestination" },
        ITEXT: { path: "FinalDestination" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "transport.finalDestination" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "transport.finalDestination" },
        G2G_UNCEFACT_XML: { path: "transport.finalDestination" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Transport/FinalDestination",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Port),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "port_name,port_code",
            valueAttribute: "port_code",
            displayAttr: "port_name,country_code",
            orderBy: "port_name:asc",
            filter_by: "country_code:!='NZ' && active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Port),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "port_name,port_code",
            valueAttribute: "port_code",
            displayAttr: "port_name,country_code",
            orderBy: "port_name:asc",
            filter_by: "country_code:!='NZ' && active:=true",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_DISCHARGE_PORT,
    {
      fuid: Fuid.REQUEST_DISCHARGE_PORT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transport.dischargePort" },
        CORE_TRANSFER_RESPONSE: { path: "transport.dischargePort.id" },
        CORE_EXPORT_INPUT: { path: "transport.dischargePort" },
        CORE_EXPORT_RESPONSE: { path: "transport.dischargePort" },
        CORE_PHYTO_INPUT: { path: "transport.dischargePort" },
        CORE_PHYTO_RESPONSE: { path: "transport.dischargePort" },
        B2G: { path: "transport.dischargePort" },
        DFE: { path: "dischargePort" },
        G2G_IPPC_PHYTO_XML: { path: "transport.dischargePort" },
        G2G_UNCEFACT_XML: { path: "transport.dischargePort" },
        B2G_SUBMISSION_PHYTO_XML: { path: "transport.dischargePort" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Transport.EntryPointID" },
        PHYTO_PDF: {
          path: `${toPhytoPdfPath(Fuid.REQUEST_DISCHARGE_PORT)}`,
        },
        ITEXT: { path: "DischargePort" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "transport.dischargePort.id" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "transport.dischargePort.id" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Transport/Ports/DischargePortID",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Transport/EntryPointID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Port),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "port_name,port_code",
            valueAttribute: "port_code",
            displayAttr: "port_code,port_name",
            orderBy: "port_name:asc",
            filter_by: "country_code:!='NZ' && active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Port),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "port_name,port_code",
            valueAttribute: "port_code",
            displayAttr: "port_code,port_name",
            orderBy: "port_name:asc",
            filter_by: "country_code:!='NZ' && active:=true",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Port),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "port_name,port_code",
            valueAttribute: "port_code",
            displayAttr: "port_code,port_name",
            orderBy: "port_name:asc",
            filter_by: "country_code:!='NZ' && active:=true",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_INSPECTION_PORT,
    {
      fuid: Fuid.REQUEST_INSPECTION_PORT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transport.inspectionPort" },
        CORE_TRANSFER_RESPONSE: { path: "transport.inspectionPort.id" },
        CORE_EXPORT_INPUT: { path: "transport.inspectionPort" },
        CORE_EXPORT_RESPONSE: { path: "transport.inspectionPort" },
        B2G: { path: "transport.inspectionPort" },
        DFE: { path: "inspectionPort" },
        ITEXT: { path: "InspectionPort" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "transport.inspectionPort.id" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "transport.inspectionPort.id" },
        G2G_UNCEFACT_XML: { path: "transport.inspectionPort" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Transport/Ports/InspectionPortID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Port),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "port_name,port_code",
            valueAttribute: "port_code",
            displayAttr: "port_code,port_name",
            orderBy: "port_name:asc",
            filter_by: "country_code:!='NZ' && active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Port),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "port_name,port_code",
            valueAttribute: "port_code",
            displayAttr: "port_code,port_name",
            orderBy: "port_name:asc",
            filter_by: "country_code:!='NZ' && active:=true",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_TRANSPORT_DEPARTURE_DATE,
    {
      fuid: Fuid.REQUEST_TRANSPORT_DEPARTURE_DATE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transport.departureDate" },
        CORE_TRANSFER_RESPONSE: { path: "transport.departureDate" },
        CORE_EXPORT_INPUT: { path: "transport.departureDate" },
        CORE_EXPORT_RESPONSE: { path: "transport.departureDate" },
        CORE_PHYTO_INPUT: { path: "transport.departureDate" },
        CORE_PHYTO_RESPONSE: { path: "transport.departureDate" },
        B2G: { path: "transport.departureDate" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Transport.DepartureDate" },
        DFE: { path: "departureDate" },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.REQUEST_TRANSPORT_DEPARTURE_DATE),
        },
        ITEXT: { path: "DepartureDate" },
        B2G_SUBMISSION_PHYTO_XML: { path: "transport.departureDate" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "transport.departureDate" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "transport.departureDate" },
        G2G_UNCEFACT_XML: { path: "transport.departureDate" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/DepartureDate",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Transport/DepartureDate",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_TRANSPORT_EXPECTED_ARRIVAL_DATE,
    {
      fuid: Fuid.REQUEST_TRANSPORT_EXPECTED_ARRIVAL_DATE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transport.expectedArrivalDate" },
        CORE_TRANSFER_RESPONSE: { path: "transport.expectedArrivalDate" },
        CORE_EXPORT_INPUT: { path: "transport.expectedArrivalDate" },
        CORE_EXPORT_RESPONSE: { path: "transport.expectedArrivalDate" },
        B2G: { path: "transport.expectedArrivalDate" },
        DFE: { path: "expectedArrivalDate" },
        ITEXT: { path: "ExpectedArrivalDate" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "transport.expectedArrivalDate",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "transport.expectedArrivalDate",
        },
        G2G_UNCEFACT_XML: { path: "transport.expectedArrivalDate" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/ExpectedArrivalDate",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_CONTAINERS_ID,
    {
      fuid: Fuid.PRODUCT_CONTAINERS_ID,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].container[*].id" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].containers[*].id" },
        CORE_EXPORT_INPUT: { path: "products[*].container[*].id" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].containers[*].id",
        },
        CORE_PHYTO_INPUT: { path: "products[*].container[*].id" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].containers[*].id",
        },
        DFE: {
          path: "products[*].container[*].id",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_CONTAINERS_NUMBER,
    {
      fuid: Fuid.PRODUCT_CONTAINERS_NUMBER,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].container[*].number" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].containers[*].number" },
        CORE_EXPORT_INPUT: { path: "products[*].container[*].number" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].containers[*].number",
        },
        CORE_PHYTO_INPUT: { path: "products[*].container[*].number" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].containers[*].number",
        },
        B2G: { path: "products[*].container[*].number" },
        DFE: {
          path: "products[*].container[*].number",
        },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].containers[*].number",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].containers[*].number",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].containers[*].number",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].Package.Containers[*].ContainerNumber",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].containers[*].${toPhytoPdfPath(Fuid.PRODUCT_CONTAINERS_NUMBER)}`,
        },
        ITEXT: { path: "ContainerNumber" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].containers[*].number",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].containers[*].number",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Containers/Container/ID",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/Package/Containers/Container/ContainerNumber",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_CONTAINERS_SEALS,
    {
      fuid: Fuid.PRODUCT_CONTAINERS_SEALS,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].container[*].seals" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].containers[*].seals" },
        CORE_EXPORT_INPUT: { path: "products[*].container[*].seals" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].containers[*].seals",
        },
        CORE_PHYTO_INPUT: { path: "products[*].container[*].seals" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].containers[*].seals",
        },
        B2G: { path: "products[*].container[*].seals" },
        DFE: {
          path: "products[*].container[*].seals",
        },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].containers[*].seals",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].containers[*].seals",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].containers[*].seals",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].Package.Containers[*].Seals[*].SealNumber",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].containers[*].${toPhytoPdfPath(Fuid.PRODUCT_CONTAINERS_SEALS)}`,
        },
        ITEXT: { path: "SealNumber" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].containers[*].seals",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].containers[*].seals",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Containers/Container/Seals/ID",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/Package/Containers/Container/Seals/Seal/SealNumber",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_UNOFFICIAL_INFORMATION,
    {
      fuid: Fuid.REQUEST_UNOFFICIAL_INFORMATION,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "unofficialInformation" },
        CORE_TRANSFER_RESPONSE: { path: "unofficialInformation" },
        CORE_PHYTO_INPUT: { path: "unofficialInformation" },
        CORE_PHYTO_RESPONSE: { path: "unofficialInformation" },
        CORE_EXPORT_INPUT: { path: "unofficialInformation" },
        CORE_EXPORT_RESPONSE: { path: "unofficialInformation" },
        DFE: { path: "unofficialInformation" },
        G2G_UNCEFACT_XML: { path: "unofficialInformation" },
        B2G_SUBMISSION_PHYTO_XML: { path: "unofficialInformation" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "UnverifiedInformation.UnverifiedText",
        },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.REQUEST_UNOFFICIAL_INFORMATION),
        },
        ITEXT: { path: "UnofficialInformation" },
        ITEXT_INPUT: { path: "unofficialInformation" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "unofficialInformation" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "unofficialInformation" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/UnverifiedInformation/UnverifiedText",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_ID,
    {
      fuid: Fuid.REQUEST_PRODUCT_ID,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].id" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].id" },
        CORE_PHYTO_INPUT: { path: "products[*].id" },
        CORE_PHYTO_RESPONSE: { path: "productItemGroupDetails.products[*].id" },
        CORE_EXPORT_INPUT: { path: "products[*].id" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].id",
        },
        DFE: { path: "products[*].id" },
        B2G: { path: "products[*].id" },
      },
    },
  ],
  [
    Fuid.PRODUCT_DESCRIPTION,
    {
      fuid: Fuid.PRODUCT_DESCRIPTION,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].itemDescription" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].itemDescription" },
        CORE_EXPORT_INPUT: { path: "products[*].itemDescription" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].itemDescription",
        },
        B2G: { path: "products[*].itemDescription" },
        DFE: { path: `products[*].itemDescription` },
        ITEXT: { path: "ProductDescription" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "products[*].itemDescription" },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].itemDescription",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].itemDescription",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Description",
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_ELIGIBILITY_COUNTRIES,
    {
      fuid: Fuid.PRODUCT_ELIGIBILITY_COUNTRIES,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].eligibilityCountries" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].eligibilityCountries",
        },
        CORE_EXPORT_INPUT: { path: "products[*].eligibilityCountries" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].eligibilityCountries",
        },
        DFE: {
          path: `products[*].eligibilityCountries`,
        },
        B2G: { path: "products[*].eligibilityCountries" },
        ITEXT: { path: "EligibilityCountry" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].eligibilityCountries",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].eligibilityCountries",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/EligibilityCountries/EligibilityCountryID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            multiple: true,
            allowBulkEntry: true,
            bulkEntryErrorMessage: "Invalid Country Code",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            multiple: true,
            allowBulkEntry: true,
            bulkEntryErrorMessage: "Invalid Country Code",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_PKG_PACKAGE_TYPE,
    {
      fuid: Fuid.PRODUCT_PKG_PACKAGE_TYPE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].packaging[*].packageType",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].packaging[*].packageType",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].packaging[*].packageType",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].packageType",
        },
        CORE_PHYTO_INPUT: {
          path: "products[*].packaging[*].packageType",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].packageType",
        },
        DFE: {
          path: `products[*].packaging[*].packageType`,
        },
        B2G: { path: "products[*].packaging[*].packageType" },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].packageType",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].packageType",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].packageType",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].Package.PackagingType",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].packaging[*].${toPhytoPdfPath(Fuid.PRODUCT_PKG_PACKAGE_TYPE)}`,
        },
        ITEXT: { path: "PackageType" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].packaging[*].packageType",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].packageType",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Packaging/Package/Type",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/Package/PackagingType",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=PKG_ECERT && active:=true`,
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=PKG_ECERT && active:=true`,
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=PKG_ECERT && active:=true`,
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_PKG_QUANTITY,
    {
      fuid: Fuid.PRODUCT_PKG_QUANTITY,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].packaging[*].quantity",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].packaging[*].quantity",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].packaging[*].quantity",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].quantity",
        },
        CORE_PHYTO_INPUT: {
          path: "products[*].packaging[*].quantity",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].quantity",
        },
        DFE: {
          path: `products[*].packaging[*].quantity`,
        },
        B2G: { path: "products[*].packaging[*].quantity" },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].quantity",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].quantity",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].quantity",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].Package.Number of Packages",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].packaging[*].${toPhytoPdfPath(Fuid.PRODUCT_PKG_QUANTITY)}`,
        },
        ITEXT: { path: "Quantity" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].packaging[*].quantity",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].quantity",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Packaging/Package/Quantity",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/Package/NumberOfPackages",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PKG_ITEM_MARK,
    {
      fuid: Fuid.PRODUCT_PKG_ITEM_MARK,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].packaging[*].itemMarksOuter",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].packaging[*].itemMarksOuter",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].packaging[*].itemMarksOuter",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].itemMarksOuter",
        },
        CORE_PHYTO_INPUT: {
          path: "products[*].packaging[*].itemMarksOuter",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].itemMarksOuter",
        },
        DFE: {
          path: `products[*].packaging[*].itemMarksOuter`,
        },
        B2G: { path: "products[*].packaging[*].itemMarksOuter" },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].itemMarksOuter",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].itemMarksOuter",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].itemMarksOuter",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].Package.PackagingDistinguishingMarks",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].packaging[*].${toPhytoPdfPath(Fuid.PRODUCT_PKG_ITEM_MARK)}`,
        },
        ITEXT: { path: "ItemMark" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].packaging[*].itemMarksOuter",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].itemMarksOuter",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Packaging/Package/ShippingMarks/Name",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/Package/PackagingDistinguishingMarks",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PKG_ITEM_MARK_CAPTION,
    {
      fuid: Fuid.PRODUCT_PKG_ITEM_MARK_CAPTION,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].packaging.itemMarksOuterCaption",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].packaging.itemMarksOuterCaption",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].packaging.itemMarksOuterCaption",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].itemMarksOuterCaption",
        },
        CORE_PHYTO_INPUT: {
          path: "products[*].packaging.itemMarksOuterCaption",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].itemMarksOuterCaption",
        },
        DFE: {
          path: `products[*].itemMarksOuterCaption`,
        },
        B2G: { path: "products[*].packaging[*].itemMarksOuterCaption" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].packaging[*].itemMarksOuterCaption",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].itemMarksOuterCaption",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].itemMarksOuterCaption",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PKG_OUTERMOST_NUMBER_OF_PACKAGES,
    {
      fuid: Fuid.PRODUCT_PKG_OUTERMOST_NUMBER_OF_PACKAGES,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].packaging.outermostNumberOfPacakges",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].packaging.outermostNumberOfPacakges",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].packaging.outermostNumberOfPacakges",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].outermostNumberOfPacakges",
        },
        CORE_PHYTO_INPUT: {
          path: "products[*].packaging.outermostNumberOfPacakges",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].outermostNumberOfPacakges",
        },
        DFE: {
          path: `products[*].outermostNumberOfPacakges`,
        },
        B2G: { path: "products[*].packaging[*].outermostNumberOfPacakges" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].packaging[*].outermostNumberOfPacakges",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].outermostNumberOfPacakges",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].outermostNumberOfPacakges",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PKG_NOMINAL_GROSS_VOLUME_VALUE,
    {
      fuid: Fuid.PRODUCT_PKG_NOMINAL_GROSS_VOLUME_VALUE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].packaging[*].nominalGrossVolume.value",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].packaging[*].nominalGrossVolume.value",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].packaging[*].nominalGrossVolume.value",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossVolume.value",
        },
        DFE: {
          path: `products[*].packaging[*].nominalGrossVolumeValue`,
        },
        B2G: { path: "products[*].packaging[*].nominalGrossVolume.value" },
        ITEXT: { path: "NominalGrossVolume" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].packaging[*].nominalGrossVolume.value",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossVolume.value",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossVolume.value",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Packaging/Package/NominalGrossVolume",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PKG_NOMINAL_GROSS_VOLUME_UNIT,
    {
      fuid: Fuid.PRODUCT_PKG_NOMINAL_GROSS_VOLUME_UNIT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].packaging[*].nominalGrossVolume.unit",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].packaging[*].nominalGrossVolume.unit",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].packaging[*].nominalGrossVolume.unit",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossVolume.unit",
        },
        DFE: {
          path: `products[*].packaging[*].nominalGrossVolumeUnit`,
        },
        B2G: { path: "products[*].packaging[*].nominalGrossVolume.unit" },
        ITEXT: { path: "NominalGrossVolumeUnit" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].packaging[*].nominalGrossVolume.unit",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossVolume.unit",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossVolume.unit",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Packaging/Package/NominalGrossVolume/@unitCode",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitVolume",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitVolume",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_PKG_NOMINAL_GROSS_WEIGHT_VALUE,
    {
      fuid: Fuid.PRODUCT_PKG_NOMINAL_GROSS_WEIGHT_VALUE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].packaging[*].nominalGrossWeight.value",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].packaging[*].nominalGrossWeight.value",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].packaging[*].nominalGrossWeight.value",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossWeight.value",
        },
        DFE: {
          path: `products[*].packaging[*].nominalGrossWeightValue`,
        },
        B2G: { path: "products[*].packaging[*].nominalGrossWeight.value" },
        ITEXT: { path: "NominalGrossWeight" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].packaging[*].nominalGrossWeight.value",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossWeight.value",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossWeight.value",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Packaging/Package/NominalGrossWeight",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PKG_NOMINAL_GROSS_WEIGHT_UNIT,
    {
      fuid: Fuid.PRODUCT_PKG_NOMINAL_GROSS_WEIGHT_UNIT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].packaging[*].nominalGrossWeight.unit",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].packaging[*].nominalGrossWeight.unit",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].packaging[*].nominalGrossWeight.unit",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossWeight.unit",
        },
        DFE: {
          path: `products[*].packaging[*].nominalGrossWeightUnit`,
        },
        B2G: { path: "products[*].packaging[*].nominalGrossWeight.unit" },
        ITEXT: { path: "NominalGrossWeightUnit" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].packaging[*].nominalGrossWeight.unit",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossWeight.unit",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].packaging[*].nominalGrossWeight.unit",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Packaging/Package/NominalGrossWeight/@unitCode",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitWeight",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitWeight",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_SCIENTIFIC_NAMES,
    {
      fuid: Fuid.PRODUCT_SCIENTIFIC_NAMES,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.scientificName" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.scientificName",
        },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.scientificName" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.scientificName",
        },
        B2G: { path: "products[*].attributes.scientificName" },
        DFE: { path: `products[*].scientificName` },
        ITEXT: { path: "ScientificName" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.scientificName",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.scientificName",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.scientificName",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/ScientificName",
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_ATTRIBUTES_ORIGIN,
    {
      fuid: Fuid.REQUEST_PRODUCT_ATTRIBUTES_ORIGIN,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "products[*].attributes.origin" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.origin",
        },
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.origin" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].attributes.origin" },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.origin" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.origin",
        },
        B2G: { path: "products[*].attributes.origin" },
        DFE: { path: `products[*].origin` },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.REQUEST_PRODUCT_ATTRIBUTES_ORIGIN)}`,
        },
        ITEXT: { path: "Origin" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.origin",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.origin",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.origin",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Origin",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=ReferenceOriginType && active:=true`,
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=ReferenceOriginType && active:=true`,
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_ATTRIBUTES_ORIGIN_COUNTRIES,
    {
      fuid: Fuid.REQUEST_PRODUCT_ATTRIBUTES_ORIGIN_COUNTRIES,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "products[*].attributes.originCountries" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.originCountries",
        },
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.originCountries" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.originCountries",
        },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.origin" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.originCountries",
        },
        B2G: { path: "products[*].attributes.originCountries" },
        DFE: { path: `products[*].originCountries` },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.originCountries",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.originCountries",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].PlaceofOrigin",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.REQUEST_PRODUCT_ATTRIBUTES_ORIGIN_COUNTRIES)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/PlaceOfOrigin",
        },
      },
      searchRepresentation: {
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_ATTRIBUTES_INSPECTION_TYPE,
    {
      fuid: Fuid.REQUEST_PRODUCT_ATTRIBUTES_INSPECTION_TYPE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.inspectionType" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.inspectionType",
        },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.inspectionType" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.inspectionType",
        },
        DFE: {
          path: `products[*].inspectionType`,
        },
        B2G: { path: "products[*].attributes.inspectionType" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.inspectionType",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.inspectionType",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.inspectionType",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/InspectionType",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=ReferenceInspectionType && active:=true`,
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=ReferenceInspectionType && active:=true`,
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_INTENDED_USES,
    {
      fuid: Fuid.PRODUCT_INTENDED_USES,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.intendedUse" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].attributes.intendedUse" },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.intendedUse" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.intendedUse",
        },
        CORE_PHYTO_INPUT: { path: "products[*].attributes.intendedUse" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.intendedUse",
        },
        DFE: {
          path: `products[*].intendedUse`,
        },
        B2G: { path: "products[*].attributes.intendedUse" },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.intendedUse",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.intendedUse",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.intendedUse",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].AdditionalProductInformation.IntendedUse",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_INTENDED_USES)}`,
        },
        ITEXT: { path: "IntendedUse" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.intendedUse",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.intendedUse",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/IntendedUse",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/AdditionalProductInformation/IntendedUse",
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_GROWERS,
    {
      fuid: Fuid.PRODUCT_PHYTO_GROWERS,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "products[*].attributes.phytoGrowers" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoGrowers",
        },
        DFE: { path: `products[*].phytoGrowers` },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoGrowers",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].AdditionalProductInformation.GrowersCode",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_GROWERS)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/AdditionalProductInformation/GrowersCode",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_ENDPOINT_INSPECTION_REF,
    {
      fuid: Fuid.PRODUCT_PHYTO_ENDPOINT_INSPECTION_REF,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoEndPointInspectionRef",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoEndPointInspectionRef",
        },
        DFE: { path: `products[*].phytoEndPointInspectionRef` },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoEndPointInspectionRef",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].AdditionalProductInformation.EndPointInspectionRef",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_ENDPOINT_INSPECTION_REF)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/AdditionalProductInformation/EndPointInspectionRef",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_SUPPLIER,
    {
      fuid: Fuid.PRODUCT_PHYTO_SUPPLIER,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoSupplier",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoSupplier",
        },
        DFE: { path: `products[*].phytoSupplier` },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoSupplier",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].AdditionalProductInformation.Supplier",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_SUPPLIER)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/AdditionalProductInformation/Supplier",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_INSPECTION_LOCATION,
    {
      fuid: Fuid.PRODUCT_PHYTO_INSPECTION_LOCATION,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoInspectionLocation",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoInspectionLocation",
        },
        DFE: { path: `products[*].phytoInspectionLocation` },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoInspectionLocation",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].AdditionalProductInformation.InspectionLocation",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_INSPECTION_LOCATION)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/AdditionalProductInformation/InspectionLocation",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_OBSERVATIONS,
    {
      fuid: Fuid.PRODUCT_PHYTO_OBSERVATIONS,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoObservation",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoObservation",
        },
        DFE: { path: `products[*].phytoObservation` },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoObservation",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].AdditionalProductInformation.Observations",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_OBSERVATIONS)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/AdditionalProductInformation/Observations",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_FIRST_EXPIRY_DATE,
    {
      fuid: Fuid.PRODUCT_FIRST_EXPIRY_DATE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.expiryDateFrom" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.expiryDateFrom",
        },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.expiryDateFrom" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.expiryDateFrom",
        },
        B2G: { path: "products[*].attributes.expiryDateFrom" },
        DFE: { path: `products[*].firstExpiryDate` },
        ITEXT: { path: "FirstExpiryDate" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.expiryDateFrom",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.expiryDateFrom",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.expiryDateFrom",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/FirstExpiryDate",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_LAST_EXPIRY_DATE,
    {
      fuid: Fuid.PRODUCT_LAST_EXPIRY_DATE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.expiryDateTo" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].attributes.expiryDateTo" },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.expiryDateTo" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.expiryDateTo",
        },
        B2G: { path: "products[*].attributes.expiryDateTo" },
        DFE: { path: `products[*].lastExpiryDate` },
        ITEXT: { path: "LastExpiryDate" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.expiryDateTo",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.expiryDateTo",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.expiryDateTo",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/LastExpiryDate",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_NET_WEIGHT_VALUE,
    {
      fuid: Fuid.PRODUCT_NET_WEIGHT_VALUE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.netWeight.value" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.netWeight.value",
        },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.netWeight.value" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.netWeight.value",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.netWeight.value",
        },
        DFE: {
          path: `products[*].netWeightValue`,
        },
        B2G: { path: "products[*].attributes.netWeight.value" },
        G2G_IPPC_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${WEIGHT_LIST_CODE}')].attributes.phytoQuantityMeasure.value`,
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.netWeight.value",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${WEIGHT_LIST_CODE}')].attributes.phytoQuantityMeasure.value`,
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Quantity.WeightType.Value",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_NET_WEIGHT_VALUE)}`,
        },
        ITEXT: { path: "NetWeight" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.netWeight.value",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.netWeight.value",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/NetWeight",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_NET_WEIGHT_UNIT,
    {
      fuid: Fuid.PRODUCT_NET_WEIGHT_UNIT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.netWeight.unit" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.netWeight.unit",
        },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.netWeight.unit" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.netWeight.unit",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.netWeight.unit",
        },
        DFE: {
          path: `products[*].netWeightUnit`,
        },
        B2G: { path: "products[*].attributes.netWeight.unit" },
        G2G_IPPC_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${WEIGHT_LIST_CODE}')].attributes.phytoQuantityMeasure.unit`,
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.netWeight.unit",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${WEIGHT_LIST_CODE}')].attributes.phytoQuantityMeasure.unit`,
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Quantity.WeightType.unitCode",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_NET_WEIGHT_UNIT)}`,
        },
        ITEXT: { path: "NetWeightUnit" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.netWeight.unit",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.netWeight.unit",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/NetWeight/@unitCode",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitWeight",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitWeight",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitWeight",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_NET_VOLUME_VALUE,
    {
      fuid: Fuid.PRODUCT_NET_VOLUME_VALUE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.netVolume.value" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.netVolume.value",
        },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.netVolume.value" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.netVolume.value",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.netVolume.value",
        },
        B2G: { path: "products[*].attributes.netVolume.value" },
        DFE: { path: `products[*].netVolumeValue` },
        G2G_IPPC_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${VOLUME_LIST_CODE}')].attributes.phytoQuantityMeasure.value`,
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.netVolume.value",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${VOLUME_LIST_CODE}')].attributes.phytoQuantityMeasure.value`,
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Quantity.VolumeType.Value",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_NET_VOLUME_VALUE)}`,
        },
        ITEXT: { path: "NetVolume" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.netVolume.value",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.netVolume.value",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/NetVolume",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_NET_VOLUME_UNIT,
    {
      fuid: Fuid.PRODUCT_NET_VOLUME_UNIT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.netVolume.unit" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.netVolume.unit",
        },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.netVolume.unit" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.netVolume.unit",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.netVolume.unit",
        },
        DFE: {
          path: `products[*].netVolumeUnit`,
        },
        B2G: { path: "products[*].attributes.netVolume.unit" },
        G2G_IPPC_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${VOLUME_LIST_CODE}')].attributes.phytoQuantityMeasure.unit`,
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.netVolume.unit",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${VOLUME_LIST_CODE}')].attributes.phytoQuantityMeasure.unit`,
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Quantity.VolumeType.unitCode",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_NET_VOLUME_UNIT)}`,
        },
        ITEXT: { path: "NetVolumeUnit" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.netVolume.unit",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.netVolume.unit",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/NetVolume/@unitCode",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitVolume",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitVolume",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitVolume",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_GROSS_WEIGHT_UNIT,
    {
      fuid: Fuid.PRODUCT_GROSS_WEIGHT_UNIT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].attributes.grossWeight.unit",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.grossWeight.unit",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].attributes.grossWeight.unit",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.grossWeight.unit",
        },
        DFE: {
          path: `products[*].grossWeightUnit`,
        },
        B2G: { path: "products[*].attributes.grossWeight.unit" },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.grossWeight.unit",
        },
        ITEXT: { path: "GrossWeightUnit" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.grossWeight.unit",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.grossWeight.unit",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/GrossWeight/@unitCode",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitWeight",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitWeight",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_GROSS_WEIGHT_VALUE,
    {
      fuid: Fuid.PRODUCT_GROSS_WEIGHT_VALUE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].attributes.grossWeight.value",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.grossWeight.value",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].attributes.grossWeight.value",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.grossWeight.value",
        },
        DFE: {
          path: `products[*].grossWeightValue`,
        },
        B2G: { path: "products[*].attributes.grossWeight.value" },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.grossWeight.value",
        },
        ITEXT: { path: "GrossWeight" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.grossWeight.value",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.grossWeight.value",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/GrossWeight",
        },
      },
    },
  ],

  [
    Fuid.PRODUCT_GROSS_VOLUME_UNIT,
    {
      fuid: Fuid.PRODUCT_GROSS_VOLUME_UNIT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].attributes.grossVolume.unit",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.grossVolume.unit",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].attributes.grossVolume.unit",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.grossVolume.unit",
        },
        DFE: {
          path: `products[*].grossVolumeUnit`,
        },
        B2G: { path: "products[*].attributes.grossVolume.unit" },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.grossVolume.unit",
        },
        ITEXT: { path: "GrossVolumeUnit" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.grossVolume.unit",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.grossVolume.unit",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/GrossVolume/@unitCode",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitVolume",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitVolume",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_GROSS_VOLUME_VALUE,
    {
      fuid: Fuid.PRODUCT_GROSS_VOLUME_VALUE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].attributes.grossVolume.value",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.grossVolume.value",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].attributes.grossVolume.value",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.grossVolume.value",
        },
        DFE: {
          path: `products[*].grossVolumeValue`,
        },
        B2G: { path: "products[*].attributes.grossVolume.value" },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.grossVolume.value",
        },
        ITEXT: { path: "GrossVolume" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.grossVolume.value",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.grossVolume.value",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/GrossVolume",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_NZHSC,
    {
      fuid: Fuid.PRODUCT_NZHSC,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.NZHSCode" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].attributes.NZHSCode" },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.NZHSCode" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.NZHSCode",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.NZHSCode",
        },
        DFE: { path: "products[*].nzhsCode" },
        ITEXT: { path: "NZHSC" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.NZHSCode",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.NZHSCode",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_ATTRIBUTES_SPECIES_COMMON_NAME,
    {
      fuid: Fuid.REQUEST_PRODUCT_ATTRIBUTES_SPECIES_COMMON_NAME,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].attributes.speciesCommonName",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.speciesCommonName",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].attributes.speciesCommonName",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.speciesCommonName",
        },
        DFE: {
          path: `products[*].speciesCommonName`,
        },
        B2G: { path: "products[*].attributes.speciesCommonName" },
        ITEXT: { path: "Species" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.speciesCommonName",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.speciesCommonName",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.speciesCommonName",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/CommonName",
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
      },
    },
  ],
  [
    Fuid.PROCESS_ID,
    {
      fuid: Fuid.PROCESS_ID,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].process[*].id" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].processes[*].id" },
        CORE_PHYTO_INPUT: { path: "products[*].process[*].id" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].id",
        },
        CORE_EXPORT_INPUT: { path: "products[*].process[*].id" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].id",
        },
        DFE: { path: "products[*].process[*].id" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "products[*].processes[*].id" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].id",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Processes/Process/ProcessTypeCode",
        },
      },
    },
  ],
  [
    Fuid.IS_SOURCE_PROCESS,
    {
      fuid: Fuid.IS_SOURCE_PROCESS,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].process[*].isSourceProcess" },
        CORE_EXPORT_INPUT: { path: "products[*].process[*].isSourceProcess" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].processes[*].isSourceProcess",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].isSourceProcess",
        },
        DFE: { path: "products[*].process[*].isSourceProcess" },
      },
    },
  ],
  [
    Fuid.PROCESS_TYPE_CODE,
    {
      fuid: Fuid.PROCESS_TYPE_CODE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].process[*].processType" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].processes[*].processType",
        },
        CORE_EXPORT_INPUT: { path: "products[*].process[*].processType" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].processType",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].processType",
        },
        CORE_PHYTO_INPUT: {
          path: "products[*].process[*].processType",
        },
        DFE: {
          path: `products[*].process[*].processType`,
        },
        B2G: { path: "products[*].process[*].processType" },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].processType",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].processType",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].processType",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Treatments[*].type_code",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].processes[*].${toPhytoPdfPath(Fuid.PROCESS_TYPE_CODE)}`,
        },
        ITEXT: { path: "Processtype_code" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].processes[*].processType",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].processType",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Processes/Process/ProcessTypeCode",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Treatments/Treatment/TypeCode",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Process),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "process_code,process_name",
            valueAttribute: "process_code",
            displayAttr: "process_name",
            orderBy: "process_name:asc",
            filter_by: "commodity_ap: ='true' && active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Process),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "process_code,process_name",
            valueAttribute: "process_code",
            displayAttr: "process_name",
            orderBy: "process_name:asc",
            filter_by: "commodity_ap: ='true' && active:=true",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Process),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "process_code,process_name",
            valueAttribute: "process_code",
            displayAttr: "process_name",
            orderBy: "process_name:asc",
            filter_by: "commodity_phyto: ='true' && active:=true",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PROCESS_START_DATE,
    {
      fuid: Fuid.PROCESS_START_DATE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].process[*].startDate" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].processes[*].startDate" },
        CORE_EXPORT_INPUT: { path: "products[*].process[*].startDate" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].startDate",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].startDate",
        },
        CORE_PHYTO_INPUT: {
          path: "products[*].process[*].startDate",
        },
        DFE: {
          path: `products[*].process[*].startDate`,
        },
        B2G: { path: "products[*].process[*].startDate" },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].startDate",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].startDate",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].startDate",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Treatments[*].TreatmentDate",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].processes[*].${toPhytoPdfPath(Fuid.PROCESS_START_DATE)}`,
        },
        ITEXT: { path: "ProcessStartDate" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].processes[*].startDate",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].startDate",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Processes/Process/StartDate",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Treatments/Treatment/TreatmentDate",
        },
      },
    },
  ],
  [
    Fuid.PROCESS_END_DATE,
    {
      fuid: Fuid.PROCESS_END_DATE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].process[*].endDate" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].processes[*].endDate" },
        CORE_EXPORT_INPUT: { path: "products[*].process[*].endDate" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].endDate",
        },
        DFE: {
          path: `products[*].process[*].endDate`,
        },
        B2G: { path: "products[*].process[*].endDate" },
        ITEXT: { path: "ProcessEndDate" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].processes[*].endDate",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].endDate",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].endDate",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Processes/Process/EndDate",
        },
      },
    },
  ],
  [
    Fuid.PROCESS_PREMISES_IDENTIFIER,
    {
      fuid: Fuid.PROCESS_PREMISES_IDENTIFIER,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].process[*].premises" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].processes[*].premises" },
        CORE_EXPORT_INPUT: { path: "products[*].process[*].premises" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].premises",
        },
        DFE: {
          path: `products[*].process[*].premises`,
        },
        B2G: { path: "products[*].process[*].premises" },
        ITEXT: { path: "PremiseIdentifier" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].processes[*].premises",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].premises",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].premises",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Processes/Process/Premise/ID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Registrations),
          usePermissions: false,
          collectionSettings: {
            queryAttributes:
              "registration_name,organisation_name,type_code,physical_address,physical_city,physical_city_postcode",
            valueAttribute: "registration_name",
            displayAttr: "organisation_name",
            orderBy: "registration_name:asc",
            filter_by: "registration_type_code:!= QCP && status:='ACTIVE'",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Registrations),
          usePermissions: false,
          collectionSettings: {
            queryAttributes:
              "registration_name,organisation_name,type_code,physical_address,physical_city,physical_city_postcode",
            valueAttribute: "registration_name",
            displayAttr: "organisation_name",
            orderBy: "registration_name:asc",
            filter_by: "registration_type_code:!= QCP && status:='ACTIVE'",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PROCESS_FOREIGN_PREMISES_IDENTIFIER,
    {
      fuid: Fuid.PROCESS_FOREIGN_PREMISES_IDENTIFIER,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].process[*].foreignPremisesID",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].processes[*].foreignPremisesIdentifier",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].process[*].foreignPremisesID",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].foreignPremisesIdentifier",
        },
        DFE: {
          path: "products[*].process[*].foreignPremisesId",
        },
        B2G: { path: "products[*].process[*].foreignPremisesID" },
        ITEXT: { path: "ForeignPremisesIdentifier" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].processes[*].foreignPremisesIdentifier",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].foreignPremisesIdentifier",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].foreignPremisesIdentifier",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Processes/Process/ForeignPremise/ForeignPremiseID",
        },
      },
    },
  ],
  [
    Fuid.PROCESS_FOREIGN_PREMISES_NAME,
    {
      fuid: Fuid.PROCESS_FOREIGN_PREMISES_NAME,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].process[*].foreignPremises",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].processes[*].foreignPremises",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].process[*].foreignPremises",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].foreignPremises",
        },
        B2G: { path: "products[*].process[*].foreignPremises" },
        DFE: { path: "products[*].process[*].foreignPremisesName" },
        ITEXT: { path: "ForeignPremisesName" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].processes[*].foreignPremise",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].foreignPremise",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].foreignPremise",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Processes/Process/ForeignPremise/ForeignPremiseName",
        },
      },
    },
  ],
  [
    Fuid.PROCESS_FOREIGN_PREMISES_COUNTRY,
    {
      fuid: Fuid.PROCESS_FOREIGN_PREMISES_COUNTRY,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].process[*].foreignPremisesCountryID",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].processes[*].foreignPremisesCountryID",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].process[*].foreignPremisesCountryID",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].foreignPremisesCountryID",
        },
        DFE: { path: "products[*].process[*].foreignPremisesCountryID" },
        B2G: { path: "products[*].process[*].foreignPremisesCountryID" },
        ITEXT: { path: "ForeignPremisesCountry" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].processes[*].countryCode",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].countryCode",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].foreignPremisesCountryID",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Processes/Process/ForeignPremise/ForeignPremiseCountryID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PROCESS_FOREIGN_PREMISES_NAME_OVERRIDE,
    {
      fuid: Fuid.PROCESS_FOREIGN_PREMISES_NAME_OVERRIDE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].process[*].foreignPremisesNameOverride",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].processes[*].nameOverride",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].process[*].foreignPremisesNameOverride",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].nameOverride",
        },
        B2G: { path: "products[*].process[*].foreignPremisesNameOverride" },
        DFE: { path: "products[*].process[*].foreignPremisesNameOverride" },
        ITEXT: { path: "ForeignPremisesNameOverride" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].processes[*].nameOverride",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].nameOverride",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Processes/Process/ForeignPremise/ForeignPremiseNameOverride",
        },
      },
    },
  ],
  [
    Fuid.PROCESS_DATE_OVERRIDE,
    {
      fuid: Fuid.PROCESS_DATE_OVERRIDE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].process[*].overrideProcessDate",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].processes[*].overrideProcessDate",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].process[*].overrideProcessDate",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].processes[*].overrideProcessDate",
        },
        DFE: {
          path: `products[*].process[*].overrideProcessDate`,
        },
        B2G: { path: "products[*].process[*].overrideProcessDate" },
        ITEXT: { path: "DateOverride" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].processes[*].overrideProcessDate",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].processes[*].overrideProcessDate",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Processes/Process/DateOverride",
        },
      },
    },
  ],
  [
    Fuid.PROCESS_SEQUENCE_NUMBER,
    {
      fuid: Fuid.PROCESS_SEQUENCE_NUMBER,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: `products[*].process[*].sequenceNumber`,
        },
        CORE_PHYTO_RESPONSE: {
          path: `productItemGroupDetails.products[*].processes[*].sequenceNumber`,
        },
        DFE: {
          path: `products[*].process[*].sequenceNumber`,
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: `productItemGroupDetails.products[*].processes[*].sequenceNumber`,
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Treatments[*].ItemNumber",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].processes[*].${toPhytoPdfPath(Fuid.PROCESS_SEQUENCE_NUMBER)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Treatments/Treatment/ItemNumber",
        },
      },
    },
  ],
  [
    Fuid.PROCESS_CHEMICAL_DETAIL,
    {
      fuid: Fuid.PROCESS_CHEMICAL_DETAIL,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: `products[*].process[*].attributes.chemicalDetail`,
        },
        CORE_PHYTO_RESPONSE: {
          path: `productItemGroupDetails.products[*].processes[*].attributes.chemicalDetail`,
        },
        DFE: {
          path: `products[*].process[*].chemicalDetail`,
        },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.chemicalDetail",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.chemicalDetail",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Treatments[*].ChemicalDetail",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].processes[*].${toPhytoPdfPath(Fuid.PROCESS_CHEMICAL_DETAIL)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Treatments/Treatment/ChemicalDetail",
        },
      },
    },
  ],
  [
    Fuid.PROCESS_DURATION_VALUE,
    {
      fuid: Fuid.PROCESS_DURATION_VALUE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: `products[*].process[*].attributes.duration.value`,
        },
        CORE_PHYTO_RESPONSE: {
          path: `productItemGroupDetails.products[*].processes[*].attributes.duration.value`,
        },
        DFE: {
          path: `products[*].process[*].durationValue`,
        },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.duration.value",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.duration.value",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Treatments[*].Duration.Value",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].processes[*].${toPhytoPdfPath(Fuid.PROCESS_DURATION_VALUE)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Treatments/Treatment/Duration/Value",
        },
      },
    },
  ],
  [
    Fuid.PROCESS_DURATION_UNIT,
    {
      fuid: Fuid.PROCESS_DURATION_UNIT,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: `products[*].process[*].attributes.duration.unit`,
        },
        CORE_PHYTO_RESPONSE: {
          path: `productItemGroupDetails.products[*].processes[*].attributes.duration.unit`,
        },
        DFE: {
          path: `products[*].process[*].durationUnit`,
        },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.duration.unit",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.duration.unit",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Treatments[*].Duration.unitCode",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].processes[*].${toPhytoPdfPath(Fuid.PROCESS_DURATION_UNIT)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Treatments/Treatment/Duration/unitCode",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitDuration",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitDuration",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitDuration",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PROCESS_TEMPERATURE_VALUE,
    {
      fuid: Fuid.PROCESS_TEMPERATURE_VALUE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: `products[*].process[*].attributes.temperature.value`,
        },
        CORE_PHYTO_RESPONSE: {
          path: `productItemGroupDetails.products[*].processes[*].attributes.temperature.value`,
        },
        DFE: {
          path: `products[*].process[*].temperatureValue`,
        },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.temperature.value",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.temperature.value",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Treatments[*].Temperature.Value",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].processes[*].${toPhytoPdfPath(Fuid.PROCESS_TEMPERATURE_VALUE)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Treatments/Treatment/Temperature/Value",
        },
      },
    },
  ],
  [
    Fuid.PROCESS_TEMPERATURE_UNIT,
    {
      fuid: Fuid.PROCESS_TEMPERATURE_UNIT,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: `products[*].process[*].attributes.temperature.unit`,
        },
        CORE_PHYTO_RESPONSE: {
          path: `productItemGroupDetails.products[*].processes[*].attributes.temperature.unit`,
        },
        DFE: {
          path: `products[*].process[*].temperatureUnit`,
        },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.temperature.unit",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.temperature.unit",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Treatments[*].Temperature.unitCode",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].processes[*].${toPhytoPdfPath(Fuid.PROCESS_TEMPERATURE_UNIT)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Treatments/Treatment/Temperature/unitCode",
        },
      },
      searchRepresentation: {
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitTemperature",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            filter_by: "list_code := unitTemperature",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PROCESS_CONCENTRATION,
    {
      fuid: Fuid.PROCESS_CONCENTRATION,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: `products[*].process[*].attributes.concentration`,
        },
        CORE_PHYTO_RESPONSE: {
          path: `productItemGroupDetails.products[*].processes[*].attributes.concentration`,
        },
        DFE: {
          path: `products[*].process[*].concentration`,
        },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.concentration",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.concentration",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Treatments[*].Concentration",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].processes[*].${toPhytoPdfPath(Fuid.PROCESS_CONCENTRATION)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Treatments/Treatment/Concentration",
        },
      },
    },
  ],
  [
    Fuid.PROCESS_ADDITIONAL_INFO,
    {
      fuid: Fuid.PROCESS_ADDITIONAL_INFO,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: `products[*].process[*].attributes.additionalTreatment`,
        },
        CORE_PHYTO_RESPONSE: {
          path: `productItemGroupDetails.products[*].processes[*].attributes.additionalTreatment`,
        },
        DFE: {
          path: `products[*].process[*].additionalTreatment`,
        },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.additionalTreatment",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].processes[*].attributes.additionalTreatment",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Treatments[*].AdditionalTreatmentInformation",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].processes[*].${toPhytoPdfPath(Fuid.PROCESS_ADDITIONAL_INFO)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Treatments/Treatment/AdditionalTreatmentInformation",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_REQUESTOR_REFERENCE,
    {
      fuid: Fuid.REQUEST_REQUESTOR_REFERENCE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "requestName" },
        CORE_PHYTO_RESPONSE: { path: "requestName" },
        CORE_TRANSFER_INPUT: { path: "requestName" },
        CORE_EXPORT_INPUT: { path: "requestName" },
        CORE_EXPORT_RESPONSE: { path: "requestName" },
        DFE: { path: "requestName" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.REQUEST_REQUESTOR_REFERENCE) },
      },
    },
  ],
  [
    Fuid.REQUEST_STATUS,
    {
      fuid: Fuid.REQUEST_STATUS,
      serviceRepresentations: {
        CORE_TRANSFER_RESPONSE: { path: "status.label" },
        CORE_EXPORT_RESPONSE: { path: "status" },
        DFE: { path: "requestStatus" },
        G2G_IPPC_PHYTO_XML: { path: "status" },
        G2G_UNCEFACT_XML: { path: "status" },
        B2G_SUBMISSION_PHYTO_XML: { path: "status" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "status" },
        SUBMISSION_PREVIEW_AP_XML_PATH: { path: "/Certificate/Status" },
      },
    },
  ],
  [
    Fuid.REQUEST_REASON_FOR_STATUS_CHANGE,
    {
      fuid: Fuid.REQUEST_REASON_FOR_STATUS_CHANGE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "reasonForStatusChange" },
        DFE: { path: "reasonForStatusChange" },
        G2G_UNCEFACT_XML: { path: "reasonForStatusChange" },
        B2G_SUBMISSION_PHYTO_XML: { path: "reasonForStatusChange" },
        ITEXT: { path: "ChangeReason" },
      },
    },
  ],
  [
    Fuid.REQUEST_TRANSIT_COUNTRIES,
    {
      fuid: Fuid.REQUEST_TRANSIT_COUNTRIES,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "transitCountries" },
        CORE_TRANSFER_RESPONSE: { path: "transitCountries" },
        CORE_EXPORT_INPUT: { path: "transitCountries" },
        CORE_EXPORT_RESPONSE: { path: "transitCountries" },
        B2G: { path: "transitCountries" },
        DFE: { path: "transitCountries" },
        ITEXT: { path: "TransitCountry" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "transitCountries" },
        G2G_UNCEFACT_XML: { path: "transitCountries" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/TransitCountries/TransitCountryID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            multiple: true,
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            multiple: true,
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PARTY_IMPORTER_ID,
    {
      fuid: Fuid.PARTY_IMPORTER_ID,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "importer.id" },
        CORE_EXPORT_INPUT: { path: "importer.id" },
        CORE_EXPORT_RESPONSE: { path: "importer.id" },
        CORE_PHYTO_INPUT: { path: "importer.id" },
        CORE_PHYTO_RESPONSE: { path: "importer.id" },
        B2G: { path: "importer.id" },
        DFE: { path: "importerId" },
        G2G_IPPC_PHYTO_XML: { path: "importer.id" },
        G2G_UNCEFACT_XML: { path: "importer.id" },
        B2G_SUBMISSION_PHYTO_XML: { path: "importer.id" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Parties.Consignee.Code" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "importer.id" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.PARTY_IMPORTER_ID) },
        ITEXT: { path: "ImporterIdentifier" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Importer/ID",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/Consignee/Code",
        },
      },
    },
  ],
  [
    Fuid.PARTY_IMPORTER_NAME,
    {
      fuid: Fuid.PARTY_IMPORTER_NAME,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "importer.name" },
        CORE_EXPORT_INPUT: { path: "importer.name" },
        CORE_EXPORT_RESPONSE: { path: "importer.name" },
        CORE_PHYTO_INPUT: { path: "importer.name" },
        CORE_PHYTO_RESPONSE: { path: "importer.name" },
        B2G: { path: "importer.name" },
        DFE: { path: "importerName" },
        G2G_IPPC_PHYTO_XML: { path: "importer.name" },
        G2G_UNCEFACT_XML: { path: "importer.name" },
        B2G_SUBMISSION_PHYTO_XML: { path: "importer.name" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Parties.Consignee.Name" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.PARTY_IMPORTER_NAME) },
        ITEXT: { path: "ImporterName" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "importer.name" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Importer/Name",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/Consignee/Name",
        },
      },
    },
  ],
  [
    Fuid.PARTY_IMPORTER_ADDRESS_LINES,
    {
      fuid: Fuid.PARTY_IMPORTER_ADDRESS_LINES,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "importer.address.lines" },
        CORE_EXPORT_INPUT: { path: "importer.address.lines" },
        CORE_EXPORT_RESPONSE: { path: "importer.address.lines" },
        CORE_PHYTO_INPUT: { path: "importer.address.lines" },
        CORE_PHYTO_RESPONSE: { path: "importer.address.lines" },
        B2G: { path: "importer.address.lines" },
        DFE: { path: "importerAddressLines" },
        G2G_IPPC_PHYTO_XML: { path: "importer.address.lines" },
        G2G_UNCEFACT_XML: { path: "importer.address.lines" },
        B2G_SUBMISSION_PHYTO_XML: { path: "importer.address.lines" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Parties.Consignee.Address.AddressLine1",
        },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.PARTY_IMPORTER_ADDRESS_LINES) },
        ITEXT: { path: "ImporterAddress" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "importer.address.lines" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Importer/Address/AddressLine1",
        },
      },
    },
  ],
  [
    Fuid.PARTY_IMPORTER_CITY,
    {
      fuid: Fuid.PARTY_IMPORTER_CITY,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "importer.address.city" },
        CORE_EXPORT_INPUT: { path: "importer.address.city" },
        CORE_EXPORT_RESPONSE: { path: "importer.address.city" },
        CORE_PHYTO_INPUT: { path: "importer.address.city" },
        CORE_PHYTO_RESPONSE: { path: "importer.address.city" },
        B2G: { path: "importer.address.city" },
        DFE: { path: "importerCity" },
        G2G_IPPC_PHYTO_XML: { path: "importer.address.city" },
        G2G_UNCEFACT_XML: { path: "importer.address.city" },
        B2G_SUBMISSION_PHYTO_XML: { path: "importer.address.city" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Parties.Consignee.Address.CityName",
        },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.PARTY_IMPORTER_CITY) },
        ITEXT: { path: "ImporterCity" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "importer.address.city" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Importer/Address/CityName",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/Consignee/Address/CityName",
        },
      },
    },
  ],
  [
    Fuid.PARTY_IMPORTER_POSTCODE,
    {
      fuid: Fuid.PARTY_IMPORTER_POSTCODE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "importer.address.postCode" },
        CORE_EXPORT_INPUT: { path: "importer.address.postCode" },
        CORE_EXPORT_RESPONSE: { path: "importer.address.postCode" },
        CORE_PHYTO_INPUT: { path: "importer.address.postCode" },
        CORE_PHYTO_RESPONSE: { path: "importer.address.postCode" },
        B2G: { path: "importer.address.postCode" },
        DFE: { path: "importerPostcode" },
        G2G_IPPC_PHYTO_XML: { path: "importer.address.postCode" },
        G2G_UNCEFACT_XML: { path: "importer.address.postCode" },
        B2G_SUBMISSION_PHYTO_XML: { path: "importer.address.postCode" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Parties.Consignee.Address.PostCode",
        },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.PARTY_IMPORTER_POSTCODE) },
        ITEXT: { path: "ImporterPostCode" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "importer.address.postCode" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Importer/Address/PostCode",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/Consignee/Address/PostCode",
        },
      },
    },
  ],
  [
    Fuid.PARTY_IMPORTER_STATE,
    {
      fuid: Fuid.PARTY_IMPORTER_STATE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "importer.address.state" },
        CORE_EXPORT_INPUT: { path: "importer.address.state" },
        CORE_EXPORT_RESPONSE: { path: "importer.address.state" },
        B2G: { path: "importer.address.state" },
        DFE: { path: "importerState" },
        G2G_UNCEFACT_XML: { path: "importer.address.state" },
        ITEXT: { path: "ImporterState" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "importer.address.state" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Importer/Address/StateName",
        },
      },
    },
  ],
  [
    Fuid.PARTY_IMPORTER_COUNTRY,
    {
      fuid: Fuid.PARTY_IMPORTER_COUNTRY,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "importer.address.countryCode" },
        CORE_EXPORT_INPUT: { path: "importer.address.countryCode" },
        CORE_EXPORT_RESPONSE: { path: "importer.address.countryCode" },
        CORE_PHYTO_INPUT: { path: "importer.address.countryCode" },
        CORE_PHYTO_RESPONSE: { path: "importer.address.countryCode" },
        B2G: { path: "importer.address.countryCode" },
        DFE: { path: "importerCountry" },
        G2G_IPPC_PHYTO_XML: { path: "importer.address.countryCode" },
        G2G_UNCEFACT_XML: { path: "importer.address.countryCode" },
        B2G_SUBMISSION_PHYTO_XML: { path: "importer.address.countryCode" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Parties.Consignee.Address.CountryID",
        },
        PHYTO_PDF: {
          path: `${toPhytoPdfPath(Fuid.PARTY_IMPORTER_COUNTRY)}`,
        },
        ITEXT: { path: "ImporterCountry" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "importer.address.countryCode.id",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Importer/Address/CountryID",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/Consignee/Address/CountryID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PARTY_IMPORT_REPRESENTATIVE,
    {
      fuid: Fuid.PARTY_IMPORT_REPRESENTATIVE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "importer.representative" },
        CORE_TRANSFER_RESPONSE: { path: "importer.representative" },
        CORE_EXPORT_INPUT: { path: "importer.representative" },
        CORE_EXPORT_RESPONSE: { path: "importer.representative" },
        B2G: { path: "importer.representative" },
        DFE: { path: "importRepresentative" },
        G2G_UNCEFACT_XML: { path: "importer.representative" },
        ITEXT: { path: "Representative" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "importer.representative" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Importer/Representative",
        },
      },
    },
  ],
  [
    Fuid.PARTY_THIRD_PARTY_CONSIGNOR_IDENTIFIER,
    {
      fuid: Fuid.PARTY_THIRD_PARTY_CONSIGNOR_IDENTIFIER,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "externalConsignor.id" },
        CORE_PHYTO_RESPONSE: { path: "externalConsignor.id" },
        B2G: { path: "thirdPartyConsignor" },
        DFE: { path: "thirdPartyConsignor" },
        G2G_IPPC_PHYTO_XML: { path: "externalConsignor.id" },
        B2G_SUBMISSION_PHYTO_XML: { path: "externalConsignor.id" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Parties.Exporter.ID" },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.PARTY_THIRD_PARTY_CONSIGNOR_IDENTIFIER),
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/Exporter/ExporterID",
        },
      },
    },
  ],
  [
    Fuid.TRANSFER_THIRD_PARTY_IDENTIFIER,
    {
      fuid: Fuid.TRANSFER_THIRD_PARTY_IDENTIFIER,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "thirdPartyConsignor" },
        CORE_TRANSFER_RESPONSE: { path: "thirdPartyConsignor" },
        CORE_EXPORT_INPUT: { path: "thirdParty" },
        CORE_EXPORT_RESPONSE: { path: "thirdParty" },
        B2G: { path: "thirdPartyConsignor" },
        DFE: { path: "tdThirdPartyConsignor" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "thirdPartyConsignor" },
        G2G_UNCEFACT_XML: { path: "thirdPartyConsignor" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "thirdPartyConsignor" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/ThirdParty/ID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Registrations),
          usePermissions: false,
          collectionSettings: {
            queryAttributes:
              "registration_name,organisation_name,type_code,physical_address,physical_city,physical_city_postcode",
            valueAttribute: "registration_name",
            displayAttr: "organisation_name",
            orderBy: "registration_name:asc",
            multiple: true,
            filter_by:
              "registration_type_code:= [RMP, EX, DEX, GFP] && status:='ACTIVE'",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Registrations),
          usePermissions: false,
          collectionSettings: {
            queryAttributes:
              "registration_name,organisation_name,type_code,physical_address,physical_city,physical_city_postcode",
            valueAttribute: "registration_name",
            displayAttr: "organisation_name",
            orderBy: "registration_name:asc",
            multiple: true,
            filter_by:
              "registration_type_code:= [RMP, EX, IPO, DEX, RCS, HS, SMO, WL, BPP, GT, AFP, AP, GFP, OPS, EAP] && status:='ACTIVE'",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_EXHAUSTED,
    {
      fuid: Fuid.REQUEST_EXHAUSTED,
      serviceRepresentations: {
        // CORE_TRANSFER_INPUT: { path: "productexhausted" },
        // CORE_TRANSFER_RESPONSE: { path: "exhausted" },
        DFE: { path: "exhausted" },
        ITEXT: { path: "EDExhausted" },
        // SUBMISSION_PREVIEW_AP_XML_PATH: { path: "/Certificate/Exhausted" },
      },
    },
  ],
  [
    Fuid.PARTY_THIRD_PARTY_CONSIGNOR_NAME,
    {
      fuid: Fuid.PARTY_THIRD_PARTY_CONSIGNOR_NAME,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "externalConsignor.name" },
        CORE_PHYTO_RESPONSE: { path: "externalConsignor.name" },
        DFE: { path: "externalConsignorName" },
        G2G_IPPC_PHYTO_XML: { path: "externalConsignor.name" },
        B2G_SUBMISSION_PHYTO_XML: { path: "externalConsignor.name" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Parties.Exporter.Name" },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.PARTY_THIRD_PARTY_CONSIGNOR_NAME),
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/Exporter/Name",
        },
      },
    },
  ],
  [
    Fuid.PARTY_THIRD_PARTY_CONSIGNOR_ADDRESS_LINES,
    {
      fuid: Fuid.PARTY_THIRD_PARTY_CONSIGNOR_ADDRESS_LINES,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "externalConsignor.address.lines" },
        CORE_PHYTO_RESPONSE: { path: "externalConsignor.address.lines" },
        DFE: { path: "externalConsignorAddressLines" },
        G2G_IPPC_PHYTO_XML: { path: "externalConsignor.address.lines" },
        B2G_SUBMISSION_PHYTO_XML: { path: "externalConsignor.address.lines" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Parties.Exporter.Address.AddressLine1",
        },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.PARTY_THIRD_PARTY_CONSIGNOR_ADDRESS_LINES),
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/Exporter/Address/AddressLine1",
        },
      },
    },
  ],
  [
    Fuid.PARTY_THIRD_PARTY_CONSIGNOR_CITY,
    {
      fuid: Fuid.PARTY_THIRD_PARTY_CONSIGNOR_CITY,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "externalConsignor.address.city" },
        CORE_PHYTO_RESPONSE: { path: "externalConsignor.address.city" },
        DFE: { path: "externalConsignorAddressCity" },
        G2G_IPPC_PHYTO_XML: { path: "externalConsignor.address.city" },
        B2G_SUBMISSION_PHYTO_XML: { path: "externalConsignor.address.city" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Parties.Exporter.Address.CityName",
        },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.PARTY_THIRD_PARTY_CONSIGNOR_CITY),
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/Exporter/Address/CityName",
        },
      },
    },
  ],
  [
    Fuid.PARTY_THIRD_PARTY_CONSIGNOR_POST_CODE,
    {
      fuid: Fuid.PARTY_THIRD_PARTY_CONSIGNOR_POST_CODE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "externalConsignor.address.postCode" },
        CORE_PHYTO_RESPONSE: { path: "externalConsignor.address.postCode" },
        DFE: { path: "externalConsignorAddressPostCode" },
        G2G_IPPC_PHYTO_XML: { path: "externalConsignor.address.postCode" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "externalConsignor.address.postCode",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Parties.Exporter.Address.PostCode",
        },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.PARTY_THIRD_PARTY_CONSIGNOR_POST_CODE),
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/Exporter/Address/PostCode",
        },
      },
    },
  ],
  [
    Fuid.PARTY_THIRD_PARTY_CONSIGNOR_COUNTRY,
    {
      fuid: Fuid.PARTY_THIRD_PARTY_CONSIGNOR_COUNTRY,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "externalConsignor.address.countryCode" },
        CORE_PHYTO_RESPONSE: { path: "externalConsignor.address.countryCode" },
        DFE: { path: "externalConsignorAddressCountryCode" },
        G2G_IPPC_PHYTO_XML: { path: "externalConsignor.address.countryCode" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "externalConsignor.address.countryCode",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Parties.Exporter.Address.CountryID",
        },
        PHYTO_PDF: {
          path: `${toPhytoPdfPath(Fuid.PARTY_THIRD_PARTY_CONSIGNOR_COUNTRY)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Parties/Exporter/Address/CountryID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PARTY_CUSTOM_TRANSIT_AGENT_ID,
    {
      fuid: Fuid.PARTY_CUSTOM_TRANSIT_AGENT_ID,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: { path: "customsTransitAgent.id" },
        CORE_EXPORT_RESPONSE: { path: "customsTransitAgent.id" },
        B2G: { path: "customsTransitAgent.id" },
        DFE: { path: "customTransitAgentId" },
        G2G_UNCEFACT_XML: { path: "customsTransitAgent.id" },
        ITEXT: { path: "CustomsTransitAgentIdentifier" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "customsTransitAgent.id" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/CustomsTransitAgent/ID",
        },
      },
    },
  ],
  [
    Fuid.PARTY_CUSTOM_TRANSIT_AGENT_NAME,
    {
      fuid: Fuid.PARTY_CUSTOM_TRANSIT_AGENT_NAME,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: { path: "customsTransitAgent.name" },
        CORE_EXPORT_RESPONSE: { path: "customsTransitAgent.name" },
        B2G: { path: "customsTransitAgent.name" },
        DFE: { path: "customTransitAgentName" },
        G2G_UNCEFACT_XML: { path: "customsTransitAgent.name" },
        ITEXT: { path: "CustomsTransitAgentName" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "customsTransitAgent.name" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/CustomsTransitAgent/Name",
        },
      },
    },
  ],
  [
    Fuid.PARTY_CUSTOM_TRANSIT_AGENT_ADDRESS_LINES,
    {
      fuid: Fuid.PARTY_CUSTOM_TRANSIT_AGENT_ADDRESS_LINES,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: { path: "customsTransitAgent.address.lines" },
        CORE_EXPORT_RESPONSE: { path: "customsTransitAgent.address.lines" },
        B2G: { path: "customsTransitAgent.address.lines" },
        DFE: { path: "customTransitAgentAddressLines" },
        G2G_UNCEFACT_XML: { path: "customsTransitAgent.address.lines" },
        ITEXT: { path: "CustomsTransitAgentAddress" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "customsTransitAgent.address.lines",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/CustomsTransitAgent/Address/AddressLine1",
        },
      },
    },
  ],
  [
    Fuid.PARTY_CUSTOM_TRANSIT_AGENT_CITY,
    {
      fuid: Fuid.PARTY_CUSTOM_TRANSIT_AGENT_CITY,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: { path: "customsTransitAgent.address.city" },
        CORE_EXPORT_RESPONSE: { path: "customsTransitAgent.address.city" },
        B2G: { path: "customsTransitAgent.address.city" },
        DFE: { path: "customTransitAgentCity" },
        G2G_UNCEFACT_XML: { path: "customsTransitAgent.address.city" },
        ITEXT: { path: "CustomsTransitAgentCity" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "customsTransitAgent.address.city",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/CustomsTransitAgent/Address/CityName",
        },
      },
    },
  ],
  [
    Fuid.PARTY_CUSTOM_TRANSIT_AGENT_POSTCODE,
    {
      fuid: Fuid.PARTY_CUSTOM_TRANSIT_AGENT_POSTCODE,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: { path: "customsTransitAgent.address.postCode" },
        CORE_EXPORT_RESPONSE: { path: "customsTransitAgent.address.postCode" },
        B2G: { path: "customsTransitAgent.address.postCode" },
        DFE: { path: "customTransitAgentPostcode" },
        G2G_UNCEFACT_XML: { path: "customsTransitAgent.address.postCode" },
        ITEXT: { path: "CustomsTransitAgentPostCode" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "customsTransitAgent.address.postCode",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/CustomsTransitAgent/Address/PostCode",
        },
      },
    },
  ],
  [
    Fuid.PARTY_CUSTOM_TRANSIT_AGENT_STATE,
    {
      fuid: Fuid.PARTY_CUSTOM_TRANSIT_AGENT_STATE,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: { path: "customsTransitAgent.address.state" },
        CORE_EXPORT_RESPONSE: { path: "customsTransitAgent.address.state" },
        B2G: { path: "customsTransitAgent.address.state" },
        DFE: { path: "customTransitAgentState" },
        G2G_UNCEFACT_XML: { path: "customsTransitAgent.address.state" },
        ITEXT: { path: "CustomsTransitAgentState" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "customsTransitAgent.address.state",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/CustomsTransitAgent/Address/StateName",
        },
      },
    },
  ],
  [
    Fuid.PARTY_CUSTOM_TRANSIT_AGENT_COUNTRY,
    {
      fuid: Fuid.PARTY_CUSTOM_TRANSIT_AGENT_COUNTRY,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: {
          path: "customsTransitAgent.address.countryCode",
        },
        CORE_EXPORT_RESPONSE: {
          path: "customsTransitAgent.address.countryCode",
        },
        B2G: { path: "customsTransitAgent.address.countryCode" },
        DFE: { path: "customTransitAgentCountry" },
        G2G_UNCEFACT_XML: { path: "customsTransitAgent.address.countryCode" },
        ITEXT: { path: "CustomsTransitAgentCountry" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "customsTransitAgent.address.countryCode.id",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/CustomsTransitAgent/Address/CountryID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PARTY_DESTINATION_IDENTIFIER,
    {
      fuid: Fuid.PARTY_DESTINATION_IDENTIFIER,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "destinationParty.id" },
        CORE_TRANSFER_RESPONSE: { path: "destinationParty.id" },
        CORE_EXPORT_INPUT: { path: "destinationParty.id" },
        CORE_EXPORT_RESPONSE: { path: "destinationParty.id" },
        B2G: { path: "destinationParty.id" },
        DFE: { path: "destinationIdentifier" },
        G2G_UNCEFACT_XML: { path: "destinationParty.id" },
        ITEXT: { path: "DestinationIdentifier" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "destinationParty.id" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Destination/ID",
        },
      },
    },
  ],
  [
    Fuid.PARTY_DESTINATION_NAME,
    {
      fuid: Fuid.PARTY_DESTINATION_NAME,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "destinationParty.name" },
        CORE_TRANSFER_RESPONSE: { path: "destinationParty.name" },
        CORE_EXPORT_INPUT: { path: "destinationParty.name" },
        CORE_EXPORT_RESPONSE: { path: "destinationParty.name" },
        B2G: { path: "destinationParty.name" },
        DFE: { path: "destinationName" },
        G2G_UNCEFACT_XML: { path: "destinationParty.name" },
        ITEXT: { path: "DestinationName" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "destinationParty.name" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Destination/Name",
        },
      },
    },
  ],
  [
    Fuid.PARTY_DESTINATION_ADDRESS,
    {
      fuid: Fuid.PARTY_DESTINATION_ADDRESS,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: { path: "destinationParty.address.lines" },
        CORE_EXPORT_RESPONSE: { path: "destinationParty.address.lines" },
        CORE_TRANSFER_INPUT: { path: "destinationParty.address.lines" },
        CORE_TRANSFER_RESPONSE: { path: "destinationParty.address.lines" },
        B2G: { path: "destinationParty.address.lines" },
        DFE: { path: "destinationAddress" },
        G2G_UNCEFACT_XML: { path: "destinationParty.address.lines" },
        ITEXT: { path: "DestinationAddress" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "destinationParty.address.lines",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Destination/Address/AddressLine1",
        },
      },
    },
  ],
  [
    Fuid.PARTY_DESTINATION_CITY,
    {
      fuid: Fuid.PARTY_DESTINATION_CITY,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "destinationParty.address.city" },
        CORE_TRANSFER_RESPONSE: { path: "destinationParty.address.city" },
        CORE_EXPORT_INPUT: { path: "destinationParty.address.city" },
        CORE_EXPORT_RESPONSE: { path: "destinationParty.address.city" },
        B2G: { path: "destinationParty.address.city" },
        DFE: { path: "destinationCity" },
        G2G_UNCEFACT_XML: { path: "destinationParty.address.city" },
        ITEXT: { path: "DestinationCity" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "destinationParty.address.city" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Destination/Address/CityName",
        },
      },
    },
  ],
  [
    Fuid.PARTY_DESTINATION_POSTCODE,
    {
      fuid: Fuid.PARTY_DESTINATION_POSTCODE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "destinationParty.address.postCode" },
        CORE_TRANSFER_RESPONSE: { path: "destinationParty.address.postCode" },
        CORE_EXPORT_INPUT: { path: "destinationParty.address.postCode" },
        CORE_EXPORT_RESPONSE: { path: "destinationParty.address.postCode" },
        B2G: { path: "destinationParty.address.postCode" },
        DFE: { path: "destinationPostcode" },
        G2G_UNCEFACT_XML: { path: "destinationParty.address.postCode" },
        ITEXT: { path: "DestinationPostCode" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "destinationParty.address.postCode",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Destination/Address/PostCode",
        },
      },
    },
  ],
  [
    Fuid.PARTY_DESTINATION_STATE,
    {
      fuid: Fuid.PARTY_DESTINATION_STATE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "destinationParty.address.state" },
        CORE_TRANSFER_RESPONSE: { path: "destinationParty.address.state" },
        CORE_EXPORT_INPUT: { path: "destinationParty.address.state" },
        CORE_EXPORT_RESPONSE: { path: "destinationParty.address.state" },
        B2G: { path: "destinationParty.address.state" },
        DFE: { path: "destinationState" },
        G2G_UNCEFACT_XML: { path: "destinationParty.address.state" },
        ITEXT: { path: "DestinationState" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "destinationParty.address.state",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Destination/Address/StateName",
        },
      },
    },
  ],
  [
    Fuid.PARTY_DESTINATION_COUNTRY,
    {
      fuid: Fuid.PARTY_DESTINATION_COUNTRY,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "destinationParty.address.countryCode" },
        CORE_TRANSFER_RESPONSE: {
          path: "destinationParty.address.countryCode",
        },
        CORE_EXPORT_INPUT: { path: "destinationParty.address.countryCode" },
        CORE_EXPORT_RESPONSE: { path: "destinationParty.address.countryCode" },
        B2G: { path: "destinationParty.address.countryCode" },
        DFE: { path: "destinationCountry" },
        G2G_UNCEFACT_XML: { path: "destinationParty.address.countryCode" },
        ITEXT: { path: "DestinationCountry" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "destinationParty.address.countryCode.id",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/Destination/Address/CountryID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_SIGNING_OFFICE,
    {
      fuid: Fuid.REQUEST_SIGNING_OFFICE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "signingOffice" },
        CORE_TRANSFER_RESPONSE: { path: "signingOffice" },
        CORE_EXPORT_INPUT: { path: "signingOffice" },
        CORE_EXPORT_RESPONSE: { path: "signingOffice" },
        DFE: { path: "signingOffice" },
        G2G_UNCEFACT_XML: { path: "signingOffice" },
        ITEXT: { path: "Signing_Office" },
        ITEXT_INPUT: { path: "signingOffice.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "signingOffice" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "signingOffice" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_DELIVERY_DETAILS,
    {
      fuid: Fuid.REQUEST_DELIVERY_DETAILS,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "deliveryDetails" },
        CORE_TRANSFER_RESPONSE: { path: "deliveryDetails" },
        CORE_EXPORT_INPUT: { path: "deliveryDetails" },
        CORE_EXPORT_RESPONSE: { path: "deliveryDetails" },
        DFE: { path: "deliveryDetails" },
        G2G_UNCEFACT_XML: { path: "deliveryDetails" },
        ITEXT: { path: "DeliveryDetails" },
        ITEXT_INPUT: { path: "deliveryDetails.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "deliveryDetails" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "deliveryDetails" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_APPROVED_EMAIL,
    {
      fuid: Fuid.REQUEST_APPROVED_EMAIL,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "approvedCertificateEmailAddresses" },
        CORE_TRANSFER_RESPONSE: { path: "approvedCertificateEmailAddresses" },
        CORE_EXPORT_INPUT: { path: "approvedCertificateEmailAddresses" },
        CORE_EXPORT_RESPONSE: { path: "approvedCertificateEmailAddresses" },
        B2G: { path: "approvedCertificateEmailAddresses" },
        DFE: { path: "approvedEmail" },
        ITEXT: { path: "ApprovedCertificateEmailAddress" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "approvedCertificateEmailAddresses",
        },
        G2G_UNCEFACT_XML: {
          path: "approvedCertificateEmailAddresses",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "approvedCertificateEmailAddresses",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/ApprovedCertificateEmailAddresses/EmailAddress",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_OFFICIAL_STATEMENT,
    {
      fuid: Fuid.REQUEST_OFFICIAL_STATEMENT,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "officialStatement" },
        CORE_PHYTO_RESPONSE: { path: "officialStatement" },
        CORE_TRANSFER_INPUT: { path: "officialStatement" },
        CORE_TRANSFER_RESPONSE: { path: "officialStatement" },
        CORE_EXPORT_INPUT: { path: "officialStatement" },
        CORE_EXPORT_RESPONSE: { path: "officialStatement" },
        DFE: { path: "officialStatement" },
        G2G_IPPC_PHYTO_XML: { path: "officialStatement" },
        G2G_UNCEFACT_XML: { path: "officialStatement" },
        B2G_SUBMISSION_PHYTO_XML: { path: "officialStatement" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.REQUEST_OFFICIAL_STATEMENT) },
        ITEXT: { path: "OfficialStatement" },
        ITEXT_INPUT: { path: "officialStatement" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "officialStatement" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "officialStatement" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/AdditionalDeclarations/AdditionalOfficialPhytosanitaryInformation",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_FREE_TEXT_DECLARATION,
    {
      fuid: Fuid.REQUEST_FREE_TEXT_DECLARATION,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "additionalDeclaration" },
        CORE_PHYTO_RESPONSE: { path: "additionalDeclaration" },
        DFE: { path: "additionalDeclaration" },
        G2G_IPPC_PHYTO_XML: { path: "additionalDeclaration" },
        B2G_SUBMISSION_PHYTO_XML: { path: "additionalDeclaration" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "AdditionalDeclarations.DeclarationText",
        },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.REQUEST_FREE_TEXT_DECLARATION) },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/AdditionalDeclarations/DeclarationText",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_INSPECTION_DATE,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_INSPECTION_DATE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "attributes.inspectionDate" },
        CORE_PHYTO_RESPONSE: { path: "attributes.inspectionDate" },
        DFE: { path: "inspectionDate" },
        G2G_IPPC_PHYTO_XML: { path: "attributes.inspectionDate" },
        B2G_SUBMISSION_PHYTO_XML: { path: "attributes.inspectionDate" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "AdditionalDeclarations.EarliestInspectionDate",
        },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.REQUEST_ATTRIBUTE_INSPECTION_DATE),
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/AdditionalDeclarations/EarliestInspectionDate",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_DELIVERY_DISPATCH_DETAILS,
    {
      fuid: Fuid.REQUEST_DELIVERY_DISPATCH_DETAILS,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "deliveryDispatchDetails" },
        CORE_TRANSFER_RESPONSE: { path: "deliveryDispatchDetails" },
        CORE_EXPORT_INPUT: { path: "deliveryDispatchDetails" },
        CORE_EXPORT_RESPONSE: { path: "deliveryDispatchDetails" },
        DFE: { path: "deliveryDispatchDetails" },
        G2G_UNCEFACT_XML: { path: "deliveryDispatchDetails" },
        ITEXT: { path: "DeliveryDispatchDetails" },
        ITEXT_INPUT: { path: "deliveryDispatchDetails.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "deliveryDispatchDetails" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "deliveryDispatchDetails" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.PRODUCT_PRODUCTION_BATCH_IDS,
    {
      fuid: Fuid.PRODUCT_PRODUCTION_BATCH_IDS,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].attributes.productionBatchIds",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].attributes.productionBatchIds",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].attributes.productionBatchIds",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.productionBatchIds",
        },
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.productionBatchIds",
        },
        CORE_PHYTO_RESPONSE: {
          path: "products[*].attributes.productionBatchIds",
        },
        DFE: { path: "products[*].productionBatchIds" },
        B2G: { path: "products[*].attributes.productBatchIds" },
        ITEXT: { path: "ProductionBatch" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.productionBatchIds",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.productionBatchIds",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.productionBatchIds",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/ProductionBatch",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PKG_PACKAGE_LEVEL,
    {
      fuid: Fuid.PRODUCT_PKG_PACKAGE_LEVEL,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].packaging[*].level",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].packaging[*].level",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].packaging[*].level",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].packaging[*].level",
        },
        B2G: { path: "products[*].packaging[*].level" },
        DFE: { path: "products[*].packaging[*].packageLevel" },
        ITEXT: { path: "PackageLevel" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].packaging.level",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].packaging.level",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].packaging.level",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Packaging/Package/Level",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_EXPORTING_COUNTRY,
    {
      fuid: Fuid.REQUEST_EXPORTING_COUNTRY,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: { path: "attributes.exportingCountryId" },
        CORE_EXPORT_RESPONSE: { path: "attributes.exportingCountryId.id" },
        G2G_UNCEFACT_XML: { path: "attributes.exportingCountryId.id" },
        B2G: { path: "attributes.exportingCountryId" },
        DFE: { path: "exportingCountryId" },
        ITEXT: { path: "ExportingCountry" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.exportingCountryId.id",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/ExportingCountryID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Countries),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "*",
            valueAttribute: "alpha_2_code",
            displayAttr: "label",
            orderBy: "label:asc",
            filter_by: "active:=true",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_CONSIGNOR,
    {
      fuid: Fuid.REQUEST_CONSIGNOR,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "consignor" },
        CORE_TRANSFER_RESPONSE: { path: "consignor" },
        DFE: { path: "consignor" },
        B2G: { path: "consignor" },
        ITEXT: { path: "ConsignorIdentifier" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "consignor.id" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/ConsignorID",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Registrations),
          usePermissions: true,
          collectionSettings: {
            queryAttributes:
              "registration_name,organisation_name,type_code,physical_address,physical_city,physical_city_postcode",
            valueAttribute: "registration_name",
            displayAttr: "organisation_name",
            orderBy: "registration_name:asc",
            filter_by:
              "registration_type_code:= [RMP, EX, DEX, GFP] && status:='ACTIVE'",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Registrations),
          usePermissions: true,
          collectionSettings: {
            queryAttributes:
              "registration_name,organisation_name,type_code,physical_address,physical_city,physical_city_postcode",
            valueAttribute: "registration_name",
            displayAttr: "organisation_name",
            orderBy: "registration_name:asc",
            filter_by:
              "registration_type_code:= [RMP, EX, IPO, DEX, RCS, HS, SMO, WL, BPP, GT, AFP, AP, GFP, OPS, EAP] && status:='ACTIVE'",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_LAST_MODIFIED_AT,
    {
      fuid: Fuid.REQUEST_LAST_MODIFIED_AT,
      serviceRepresentations: {
        B2G: { path: "lastModifiedAt" },
        DFE: { path: "lastModifiedAt" },
        G2G_IPPC_PHYTO_XML: { path: "lastModifiedAt" },
        G2G_UNCEFACT_XML: { path: "lastModifiedAt" },
        B2G_SUBMISSION_PHYTO_XML: { path: "lastModifiedAt" },
        ITEXT: { path: "LastUpdatedDate" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "updatedAt" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/LastUpdatedDate",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_STATUS_LABEL,
    {
      fuid: Fuid.REQUEST_STATUS_LABEL,
      serviceRepresentations: {
        B2G: { path: "status" },
        DFE: { path: "statusLabel" },
        ITEXT: { path: "Status" },
        G2G_UNCEFACT_XML: { path: "statusLabel" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "statusLabel" },
        SUBMISSION_PREVIEW_AP_XML_PATH: { path: "/Certificate/Status" },
      },
    },
  ],
  [
    Fuid.PRODUCT_SOURCE_ID,
    {
      fuid: Fuid.PRODUCT_SOURCE_ID,
      serviceRepresentations: {
        CORE_TRANSFER_RESPONSE: { path: "products[*].sources[*].id" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].sources[*].id",
        },
        DFE: { path: "products[*].sources[*].sourceId" },
      },
    },
  ],
  [
    Fuid.PRODUCT_SOURCE_ITEM_DESCRIPTION,
    {
      fuid: Fuid.PRODUCT_SOURCE_ITEM_DESCRIPTION,
      serviceRepresentations: {
        DFE: { path: "products[*].sources[*].productName" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].sources[*].details.itemDescription.name",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].sources[*].details.itemDescription.name",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_SOURCE_TRANSFER_REQUEST_ID,
    {
      fuid: Fuid.PRODUCT_SOURCE_TRANSFER_REQUEST_ID,
      serviceRepresentations: {
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].sources[*].transferRequestId",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].sources[*].transferRequestId",
        },
        DFE: {
          path: "products[*].sources[*].transferRequestId",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_SOURCE_TRANSFER_NUMBER,
    {
      fuid: Fuid.PRODUCT_SOURCE_TRANSFER_NUMBER,
      serviceRepresentations: {
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].sources[*].transferRequestNumber",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].sourceProducts[*].requestNumber",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].sources[*].transferRequestNumber",
        },
        CORE_TRANSFER_INPUT: {
          path: "products[*].sourceProducts[*].requestNumber",
        },
        DFE: {
          path: "products[*].sources[*].certificateNumber",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_SOURCE_REQUEST_NUMBER,
    {
      fuid: Fuid.PRODUCT_SOURCE_REQUEST_NUMBER,
      serviceRepresentations: {
        B2G: { path: "products[*].sourceProducts[*].requestNumber" },
        DFE: { path: "products[*].sources[*].requestNumber" },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].sources[*].transferRequestNumber",
        },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].sources[*].transferRequestNumber",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].sources[*].transferRequestNumber",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/SourceDocuments/SourceDocument/ID",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_SOURCE_SEQUENCE_NUMBER,
    {
      fuid: Fuid.PRODUCT_SOURCE_SEQUENCE_NUMBER,
      serviceRepresentations: {
        B2G: { path: "products[*].sourceProducts[*].sequenceNumber" },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].sources[*].sequenceNumber",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].sourceProducts[*].sequenceNumber",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].sources[*].sequenceNumber",
        },
        CORE_TRANSFER_INPUT: {
          path: "products[*].sourceProducts[*].sequenceNumber",
        },
        DFE: {
          path: "products[*].sources[*].productSequenceNumber",
        },
        ITEXT: { path: "SequenceNumber" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].sources[*].sequenceNumber",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].sources[*].sequenceNumber",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].sources[*].sequenceNumber",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/SourceDocuments/SourceDocument/ProductItemReference",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_DECLARATION_STATEMENT_CODE,
    {
      fuid: Fuid.REQUEST_DECLARATION_STATEMENT_CODE,
      serviceRepresentations: {
        B2G: { path: "declarations[*].declarationStatementCode" },
      },
    },
  ],
  [
    Fuid.REQUEST_DECLARATION_STATEMENT_VALUE,
    {
      fuid: Fuid.REQUEST_DECLARATION_STATEMENT_VALUE,
      serviceRepresentations: {
        B2G: { path: "declarations[*].inputs[*].inputValue" },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_CONTRACT_NO,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_CONTRACT_NO,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.contractNo" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.contractNo" },
        CORE_EXPORT_INPUT: { path: "attributes.contractNo" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.contractNo",
        },
        B2G: { path: "attributes.contractNo" },
        DFE: { path: "contractNo" },
        G2G_UNCEFACT_XML: { path: "attributes.contractNo" },
        ITEXT: { path: "ContractNo" },
        ITEXT_INPUT: { path: "attributes.contractNo.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.contractNo" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.contractNo" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_EU_COMMODITY_CODE,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_EU_COMMODITY_CODE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.euCommodityCode" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.euCommodityCode" },
        CORE_EXPORT_INPUT: { path: "attributes.euCommodityCode" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.euCommodityCode",
        },
        B2G: { path: "attributes.euCommodityCode" },
        DFE: { path: "euCommodityCode" },
        G2G_UNCEFACT_XML: { path: "attributes.euCommodityCode" },
        ITEXT: { path: "EUCommodityCode" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.euCommodityCode" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.euCommodityCode" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_EU_TRANSIT,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_EU_TRANSIT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.euTransit" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.euTransit" },
        CORE_EXPORT_INPUT: { path: "attributes.euTransit" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.euTransit",
        },
        B2G: { path: "attributes.euTransit" },
        DFE: { path: "euTransit" },
        ITEXT: { path: "TransitThroughEU" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.euTransit" },
        G2G_UNCEFACT_XML: { path: "attributes.euTransit" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.euTransit" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_EXPORTER_REF,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_EXPORTER_REF,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.exporterRef" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.exporterRef" },
        CORE_EXPORT_INPUT: { path: "attributes.exporterRef" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.exporterRef",
        },
        B2G: { path: "attributes.exporterRef" },
        DFE: { path: "exporterRef" },
        G2G_UNCEFACT_XML: { path: "attributes.exporterRef" },
        ITEXT: { path: "Exporter_Ref" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.exporterRef" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.exporterRef" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_BRAND_NAME,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_BRAND_NAME,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.brandName" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.brandName" },
        CORE_EXPORT_INPUT: { path: "attributes.brandName" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.brandName",
        },
        B2G: { path: "attributes.brandName" },
        DFE: { path: "brandName" },
        G2G_UNCEFACT_XML: { path: "attributes.brandName" },
        ITEXT: { path: "BrandName" },
        ITEXT_INPUT: { path: "attributes.brandName.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.brandName" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.brandName" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_EXP_REF,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_EXP_REF,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.expRef" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.expRef" },
        CORE_EXPORT_INPUT: { path: "attributes.expRef" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.expRef",
        },
        B2G: { path: "attributes.expRef" },
        DFE: { path: "expRef" },
        G2G_UNCEFACT_XML: { path: "attributes.expRef" },
        ITEXT: { path: "ExpRef" },
        ITEXT_INPUT: { path: "attributes.expRef.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.expRef" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.expRef" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_CATEGORY_OF_GOODS,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_CATEGORY_OF_GOODS,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.categoryOfGoods" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.categoryOfGoods" },
        CORE_EXPORT_INPUT: { path: "attributes.categoryOfGoods" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.categoryOfGoods",
        },
        B2G: { path: "attributes.categoryOfGoods" },
        DFE: { path: "categoryOfGoods" },
        G2G_UNCEFACT_XML: { path: "attributes.categoryOfGoods" },
        ITEXT: { path: "Categoryofgoods" },
        ITEXT_INPUT: { path: "attributes.categoryOfGoods.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.categoryOfGoods" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.categoryOfGoods" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_ADDRESS,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_ADDRESS,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.address" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.address" },
        CORE_EXPORT_INPUT: { path: "attributes.address" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.address",
        },
        B2G: { path: "attributes.address" },
        DFE: { path: "address" },
        G2G_UNCEFACT_XML: { path: "attributes.address" },
        ITEXT: { path: "Address" },
        ITEXT_INPUT: { path: "attributes.address.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.address" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.address" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_CN_INSPECTION,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_CN_INSPECTION,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.cnInspection" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.cnInspection" },
        CORE_EXPORT_INPUT: { path: "attributes.cnInspection" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.cnInspection",
        },
        B2G: { path: "attributes.cnInspection" },
        DFE: { path: "cnInspection" },
        G2G_UNCEFACT_XML: { path: "attributes.cnInspection" },
        ITEXT: { path: "CNinspection" },
        ITEXT_INPUT: { path: "attributes.cnInspection.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.cnInspection" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.cnInspection" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_ADMINISTRATIVE_TERRITORIAL_UNIT,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_ADMINISTRATIVE_TERRITORIAL_UNIT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "attributes.administrativeTerritorialUnit",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "attributes.administrativeTerritorialUnit",
        },
        CORE_EXPORT_INPUT: {
          path: "attributes.administrativeTerritorialUnit",
        },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.administrativeTerritorialUnit",
        },
        B2G: {
          path: "attributes.administrativeTerritorialUnit",
        },
        DFE: {
          path: "administrativeTerritorialUnit",
        },
        G2G_UNCEFACT_XML: {
          path: "attributes.administrativeTerritorialUnit",
        },
        ITEXT: { path: "AdministrativeTerritorialUnit" },
        ITEXT_INPUT: { path: "attributes.administrativeTerritorialUnit.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.administrativeTerritorialUnit",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.administrativeTerritorialUnit",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_FOR_IMPORT_OR_ADMISSION,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_FOR_IMPORT_OR_ADMISSION,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.forImportorAdmission" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.forImportorAdmission" },
        CORE_EXPORT_INPUT: { path: "attributes.forImportorAdmission" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.forImportorAdmission",
        },
        B2G: { path: "attributes.forImportorAdmission" },
        DFE: { path: "forImportorAdmission" },
        G2G_UNCEFACT_XML: { path: "attributes.forImportorAdmission" },
        ITEXT: { path: "Forimportoradmission" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.forImportorAdmission",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.forImportorAdmission",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_HALAL_AUDITOR,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_HALAL_AUDITOR,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.halalAuditor" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.halalAuditor" },
        CORE_EXPORT_INPUT: { path: "attributes.halalAuditor" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.halalAuditor",
        },
        B2G: { path: "attributes.halalAuditor" },
        DFE: { path: "halalAuditor" },
        G2G_UNCEFACT_XML: { path: "attributes.halalAuditor" },
        ITEXT: { path: "HalalAuditor" },
        ITEXT_INPUT: { path: "attributes.halalAuditor.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.halalAuditor" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.halalAuditor" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_HALAL_CERTIFICATE_NO,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_HALAL_CERTIFICATE_NO,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.halalCertificateNumber" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.halalCertificateNumber" },
        CORE_EXPORT_INPUT: { path: "attributes.halalCertificateNumber" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.halalCertificateNumber",
        },
        B2G: { path: "attributes.halalCertificateNumber" },
        DFE: { path: "halalCertificateNumber" },
        G2G_UNCEFACT_XML: { path: "attributes.halalCertificateNumber" },
        ITEXT: { path: "Halalcertificatenumber" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.halalCertificateNumber",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.halalCertificateNumber",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_ISSUING_AUTHORITY,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_ISSUING_AUTHORITY,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.issuingAuthority" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.issuingAuthority" },
        CORE_EXPORT_INPUT: { path: "attributes.issuingAuthority" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.issuingAuthority",
        },
        B2G: { path: "attributes.issuingAuthority" },
        DFE: { path: "issuingAuthority" },
        G2G_UNCEFACT_XML: { path: "attributes.issuingAuthority" },
        ITEXT: { path: "IssuingAuthority" },
        ITEXT_INPUT: { path: "attributes.issuingAuthority.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.issuingAuthority" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.issuingAuthority" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_ISSUING_AUTHORITY_1,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_ISSUING_AUTHORITY_1,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.issuingAuthority1" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.issuingAuthority1" },
        CORE_EXPORT_INPUT: { path: "attributes.issuingAuthority1" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.issuingAuthority1",
        },
        B2G: { path: "attributes.issuingAuthority1" },
        DFE: { path: "issuingAuthority1" },
        G2G_UNCEFACT_XML: { path: "attributes.issuingAuthority1" },
        ITEXT: { path: "IssuingAuthority1" },
        ITEXT_INPUT: { path: "attributes.issuingAuthority1.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.issuingAuthority1",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.issuingAuthority1",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_LOT,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_LOT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.lot" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.lot" },
        CORE_EXPORT_INPUT: { path: "attributes.lot" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.lot",
        },
        CORE_PHYTO_RESPONSE: { path: "attributes.lot" },
        B2G: { path: "attributes.lot" },
        DFE: { path: "lot" },
        G2G_UNCEFACT_XML: { path: "attributes.lot" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.REQUEST_ATTRIBUTE_LOT) },
        ITEXT: { path: "Lot" },
        ITEXT_INPUT: { path: "attributes.lot.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.lot" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.lot" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_MANUFACTURER_REGISTRATION,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_MANUFACTURER_REGISTRATION,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.manufacturerRegistration" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.manufacturerRegistration" },
        CORE_EXPORT_INPUT: { path: "attributes.manufacturerRegistration" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.manufacturerRegistration",
        },
        B2G: { path: "attributes.manufacturerRegistration" },
        DFE: { path: "manufacturerRegistration" },
        G2G_UNCEFACT_XML: { path: "attributes.manufacturerRegistration" },
        ITEXT: { path: "ManufacturerRegistration" },
        ITEXT_INPUT: { path: "attributes.manufacturerRegistration.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.manufacturerRegistration",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.manufacturerRegistration",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_MANUFACTURING_PREMISES,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_MANUFACTURING_PREMISES,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.manifacturingpremisesname" },
        CORE_TRANSFER_RESPONSE: {
          path: "attributes.manifacturingpremisesname",
        },
        CORE_EXPORT_INPUT: { path: "attributes.manifacturingpremisesname" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.manifacturingpremisesname",
        },
        B2G: { path: "attributes.manifacturingpremisesname" },
        DFE: { path: "manifacturingpremisesname" },
        G2G_UNCEFACT_XML: { path: "attributes.manifacturingpremisesname" },
        ITEXT: { path: "Manufacturingpremisesname" },
        ITEXT_INPUT: { path: "attributes.manifacturingpremisesname.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.manifacturingpremisesname",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.manifacturingpremisesname",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_MARKING_OF_SEAL_ON_TRANSPORT_CONTAI,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_MARKING_OF_SEAL_ON_TRANSPORT_CONTAI,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "attributes.markingofsealontransportcontai",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "attributes.markingofsealontransportcontai",
        },
        CORE_EXPORT_INPUT: {
          path: "attributes.markingofsealontransportcontai",
        },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.markingofsealontransportcontai",
        },
        B2G: {
          path: "attributes.markingofsealontransportcontai",
        },
        DFE: {
          path: "markingofsealontransportcontai",
        },
        ITEXT: { path: "Markingofsealontransportcontai" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.markingofsealontransportcontai",
        },
        G2G_UNCEFACT_XML: {
          path: "attributes.markingofsealontransportcontai",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.markingofsealontransportcontai",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_NAME_REGISTRATION_AND_ADDRESS_SE,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_NAME_REGISTRATION_AND_ADDRESS_SE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "attributes.nameRegistrationandAddressofse",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "attributes.nameRegistrationandAddressofse",
        },
        CORE_EXPORT_INPUT: {
          path: "attributes.nameRegistrationandAddressofse",
        },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.nameRegistrationandAddressofse",
        },
        B2G: {
          path: "attributes.nameRegistrationandAddressofse",
        },
        DFE: {
          path: "nameRegistrationandAddressofse",
        },
        ITEXT: { path: "Nameregistrationandaddressofse" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.nameRegistrationandAddressofse",
        },
        G2G_UNCEFACT_XML: {
          path: "attributes.nameRegistrationandAddressofse",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.nameRegistrationandAddressofse",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_NET_WEIGHT_IN_WORDS,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_NET_WEIGHT_IN_WORDS,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.netWeightInWords" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.netWeightInWords" },
        CORE_EXPORT_INPUT: { path: "attributes.netWeightInWords" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.netWeightInWords",
        },
        B2G: { path: "attributes.netWeightInWords" },
        DFE: { path: "netWeightInWords" },
        G2G_UNCEFACT_XML: { path: "attributes.netWeightInWords" },
        ITEXT: { path: "NetWeightInWords" },
        ITEXT_INPUT: { path: "attributes.netWeightInWords.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.netWeightInWords" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.netWeightInWords" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_NUMBER_OF_DOSES,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_NUMBER_OF_DOSES,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.numberOfDoses" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.numberOfDoses" },
        CORE_EXPORT_INPUT: { path: "attributes.numberOfDoses" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.numberOfDoses",
        },
        B2G: { path: "attributes.numberOfDoses" },
        DFE: { path: "numberOfDoses" },
        ITEXT: { path: "NumberofDoses" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.numberOfDoses" },
        G2G_UNCEFACT_XML: { path: "attributes.numberOfDoses" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.numberOfDoses" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_NUMBER_OF_EMBRYOS,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_NUMBER_OF_EMBRYOS,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.numberOfEmbryos" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.numberOfEmbryos" },
        CORE_EXPORT_INPUT: { path: "attributes.numberOfEmbryos" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.numberOfEmbryos",
        },
        B2G: { path: "attributes.numberOfEmbryos" },
        DFE: { path: "numberOfEmbryos" },
        ITEXT: { path: "NumberofEmbryos" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.numberOfEmbryos" },
        G2G_UNCEFACT_XML: { path: "attributes.numberOfEmbryos" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.numberOfEmbryos" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_NUMBER_OF_PACKAGES_CONTAINER,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_NUMBER_OF_PACKAGES_CONTAINER,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.numberOfPackagesContainers" },
        CORE_TRANSFER_RESPONSE: {
          path: "attributes.numberOfPackagesContainers",
        },
        CORE_EXPORT_INPUT: { path: "attributes.numberOfPackagesContainers" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.numberOfPackagesContainers",
        },
        B2G: { path: "attributes.numberOfPackagesContainers" },
        DFE: { path: "numberOfPackagesContainers" },
        ITEXT: { path: "NumberofPackagescontainers" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.numberOfPackagesContainers",
        },
        G2G_UNCEFACT_XML: {
          path: "attributes.numberOfPackagesContainers",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.numberOfPackagesContainers",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_PLACE_AND_DATE_OF_QUARANTINE,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_PLACE_AND_DATE_OF_QUARANTINE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.placeandDateofQuarantine" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.placeandDateofQuarantine" },
        CORE_EXPORT_INPUT: { path: "attributes.placeandDateofQuarantine" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.placeandDateofQuarantine",
        },
        B2G: { path: "attributes.placeandDateofQuarantine" },
        DFE: { path: "placeandDateofQuarantine" },
        G2G_UNCEFACT_XML: { path: "attributes.placeandDateofQuarantine" },
        ITEXT: { path: "PlaceandDateofQuarantine" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.placeandDateofQuarantine",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.placeandDateofQuarantine",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_PRODUCT_DESCRIPTION,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_PRODUCT_DESCRIPTION,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.productDescription" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.productDescription" },
        CORE_EXPORT_INPUT: { path: "attributes.productDescription" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.productDescription",
        },
        B2G: { path: "attributes.productDescription" },
        DFE: { path: "productDescription" },
        G2G_UNCEFACT_XML: { path: "attributes.productDescription" },
        ITEXT: { path: "ProductDescription_1" },
        ITEXT_INPUT: { path: "attributes.productDescription.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.productDescription",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.productDescription",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_PURPOSE_BR,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_PURPOSE_BR,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.purposeBR" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.purposeBR" },
        CORE_EXPORT_INPUT: { path: "attributes.purposeBR" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.purposeBR",
        },
        B2G: { path: "attributes.purposeBR" },
        DFE: { path: "purposeBR" },
        G2G_UNCEFACT_XML: { path: "attributes.purposeBR" },
        ITEXT: { path: "PurposeBR" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.purposeBR" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.purposeBR" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_SANITARY_CERTIFICATE_NUMBER,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_SANITARY_CERTIFICATE_NUMBER,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.sanitoryCertificateNumber" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.halalCertificateNumber" },
        CORE_EXPORT_INPUT: { path: "attributes.sanitoryCertificateNumber" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.sanitoryCertificateNumber",
        },
        B2G: { path: "attributes.sanitoryCertificateNumber" },
        DFE: { path: "sanitoryCertificateNumber" },
        G2G_UNCEFACT_XML: { path: "attributes.sanitoryCertificateNumber" },
        ITEXT: { path: "SanitaryCertificateNumber" },
        ITEXT_INPUT: { path: "attributes.sanitoryCertificateNumber.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.sanitoryCertificateNumber",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.sanitoryCertificateNumber",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_SCOPE,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_SCOPE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.scope" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.scope" },
        CORE_EXPORT_INPUT: { path: "attributes.scope" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.scope",
        },
        B2G: { path: "attributes.scope" },
        DFE: { path: "scope" },
        G2G_UNCEFACT_XML: { path: "attributes.scope" },
        ITEXT: { path: "Scope" },
        ITEXT_INPUT: { path: "attributes.scope.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.scope" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.scope" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_SUMMARY_OF_RECORDS_FOR,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_SUMMARY_OF_RECORDS_FOR,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.summaryofRecordsfor" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.summaryofRecordsfor" },
        CORE_EXPORT_INPUT: { path: "attributes.summaryofRecordsfor" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.summaryofRecordsfor",
        },
        B2G: { path: "attributes.summaryofRecordsfor" },
        DFE: { path: "summaryofRecordsfor" },
        ITEXT: { path: "Summaryofrecordsfor" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.summaryofRecordsfor",
        },
        G2G_UNCEFACT_XML: {
          path: "attributes.summaryofRecordsfor",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "attributes.summaryofRecordsfor",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_TRANSPORT,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_TRANSPORT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.transport" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.transport" },
        CORE_EXPORT_INPUT: { path: "attributes.transport" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.transport",
        },
        B2G: { path: "attributes.transport" },
        DFE: { path: "transport" },
        G2G_UNCEFACT_XML: { path: "attributes.transport" },
        ITEXT: { path: "Transport" },
        ITEXT_INPUT: { path: "attributes.transport.name" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.transport" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.transport" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_FOR_TOTAL_QUANTITY,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_FOR_TOTAL_QUANTITY,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.totalQuantity" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.totalQuantity" },
        CORE_EXPORT_INPUT: { path: "attributes.totalQuantity" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.totalQuantity",
        },
        B2G: { path: "attributes.totalQuantity" },
        DFE: { path: "totalQuantity" },
        G2G_UNCEFACT_XML: { path: "attributes.totalQuantity" },
        ITEXT: { path: "TotalQuantity" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.totalQuantity" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.totalQuantity" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_FOR_TOTAL_GROSS_WEIGHT,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_FOR_TOTAL_GROSS_WEIGHT,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.totalGrossWeight" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.totalGrossWeight" },
        CORE_EXPORT_INPUT: { path: "attributes.totalGrossWeight" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.totalGrossWeight",
        },
        B2G: { path: "attributes.totalGrossWeight" },
        DFE: { path: "totalGrossWeight" },
        G2G_UNCEFACT_XML: { path: "attributes.totalGrossWeight" },
        ITEXT: { path: "TotalGrossWeight" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.totalGrossWeight" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.totalGrossWeight" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_ED_REFERENCE,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_ED_REFERENCE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.edReference" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.edReference" },
        CORE_EXPORT_INPUT: { path: "attributes.edReference" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.edReference",
        },
        B2G: { path: "attributes.edReference" },
        DFE: { path: "edReference" },
        G2G_UNCEFACT_XML: { path: "attributes.edReference" },
        ITEXT: { path: "EDReference" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "attributes.edReference" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.edReference" },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_EXPORTER_SIGNATURE,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_EXPORTER_SIGNATURE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "attributes.exporterSignature" },
        CORE_TRANSFER_RESPONSE: { path: "attributes.exporterSignature" },
        CORE_EXPORT_INPUT: { path: "attributes.exporterSignature" },
        CORE_EXPORT_RESPONSE: {
          path: "attributes.exporterSignature",
        },
        B2G: { path: "attributes.exporterSignature" },
        DFE: { path: "exporterSignature" },
        G2G_UNCEFACT_XML: { path: "attributes.exporterSignature" },
        ITEXT: { path: "ExporterSignature" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "attributes.exporterSignature",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.exporterSignature" },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=ExporterSignature && active:=true`,
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=ExporterSignature && active:=true`,
          },
        },
      },
      coreRepresentation: { AP_EXPORT: true, AP_TRANSFER: true },
    },
  ],
  [
    Fuid.PRODUCT_PRODUCT_REFERENCE,
    {
      fuid: Fuid.PRODUCT_PRODUCT_REFERENCE,
      serviceRepresentations: {
        B2G: { path: "products[*].attributes.productReference" },
        CORE_TRANSFER_INPUT: { path: "products[*].productReference" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].productReference" },
        CORE_EXPORT_INPUT: { path: "products[*].productReference" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].productReference",
        },
        DFE: { path: "products[*].productReference" },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].productReference",
        },
        ITEXT: { path: "ProductReference" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].productReference",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].productReference",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PRODUCT_STATEMENT,
    {
      fuid: Fuid.PRODUCT_PRODUCT_STATEMENT,
      serviceRepresentations: {
        B2G: { path: "products[*].attributes.productStatement" },
        CORE_TRANSFER_INPUT: { path: "products[*].productStatement" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].productStatement" },
        CORE_EXPORT_INPUT: { path: "products[*].productStatement" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].productStatement",
        },
        DFE: { path: "products[*].productStatement" },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].productStatement",
        },
        ITEXT: { path: "ProductStatement" },
        ITEXT_INPUT: { path: "productStatement" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].productStatement",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].productStatement",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_CLS_TYPE,
    {
      fuid: Fuid.PRODUCT_CLS_TYPE,
      serviceRepresentations: {
        B2G: { path: "products[*].classification[*].classificationCode" },
      },
    },
  ],
  [
    Fuid.PRODUCT_CLS_VALUE,
    {
      fuid: Fuid.PRODUCT_CLS_VALUE,
      serviceRepresentations: {
        B2G: { path: "products[*].classification[*].value" },
      },
    },
  ],
  [
    Fuid.PRODUCT_ITEM_SEQUENCE_NUMBER,
    {
      fuid: Fuid.PRODUCT_ITEM_SEQUENCE_NUMBER,
      serviceRepresentations: {
        B2G: { path: "products[*].id" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].sequenceNumber",
        },
        CORE_TRANSFER_INPUT: { path: "products[*].sequenceNumber" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].sequenceNumber" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].sequenceNumber",
        },
        DFE: { path: "products[*].sequenceNumber" },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].sequenceNumber",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].sequenceNumber",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].ItemNumber",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_ITEM_SEQUENCE_NUMBER)}`,
        },
        ITEXT: { path: "ProductSequenceNumber" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "products[*].sequenceNumber" },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].sequenceNumber",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItem",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/ItemNumber",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_PHYTO_BOTANICAL_NAME,
    {
      fuid: Fuid.REQUEST_PRODUCT_PHYTO_BOTANICAL_NAME,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "products[*].attributes.phytoBotanicalName" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoBotanicalName",
        },
        DFE: { path: "products[*].phytoBotanicalName" },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoBotanicalName",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoBotanicalName",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].BotanicalName",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.REQUEST_PRODUCT_PHYTO_BOTANICAL_NAME)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/BotanicalName",
        },
      },
      searchRepresentation: {
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.PhytoBotanicalName),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "botanical_name",
            valueAttribute: "id",
            displayAttr: "botanical_name",
            orderBy: "botanical_name:asc",
            filter_by: "active:=true",
            dependOn: Fuid.REQUEST_PRODUCT_PHYTO_COMMON_NAME,
            dependencyFilterKey: "id",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_PHYTO_COMMON_NAME,
    {
      fuid: Fuid.REQUEST_PRODUCT_PHYTO_COMMON_NAME,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "products[*].attributes.phytoCommonName" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoCommonName",
        },
        DFE: { path: "products[*].phytoCommonName" },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoCommonName",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoCommonName",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].CommonName",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.REQUEST_PRODUCT_PHYTO_COMMON_NAME)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/CommonName",
        },
      },
      searchRepresentation: {
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.PhytoBotanicalName),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "common_name",
            valueAttribute: "id",
            displayAttr: "common_name",
            orderBy: "common_name:asc",
            filter_by: "common_name:!~~ && active:=true",
            dependOn: Fuid.REQUEST_PRODUCT_PHYTO_BOTANICAL_NAME,
            dependencyFilterKey: "id",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_PHYTO_CLASS,
    {
      fuid: Fuid.REQUEST_PRODUCT_PHYTO_CLASS,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "products[*].attributes.phytoClass" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoClass",
        },
        DFE: { path: "products[*].phytoClass" },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoClass",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoClass",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Class",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.REQUEST_PRODUCT_PHYTO_CLASS)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Class",
        },
      },
      searchRepresentation: {
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=Class && active:=true`,
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_PHYTO_SUB_CLASS,
    {
      fuid: Fuid.REQUEST_PRODUCT_PHYTO_SUB_CLASS,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "products[*].attributes.phytoSubClass" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoSubClass",
        },
        DFE: { path: "products[*].phytoSubClass" },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoSubClass",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoSubClass",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].SubClass",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.REQUEST_PRODUCT_PHYTO_SUB_CLASS)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/SubClass",
        },
      },
      searchRepresentation: {
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=SubClass && active:=true`,
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_PHYTO_QUANTITY_TYPE,
    {
      fuid: Fuid.REQUEST_PRODUCT_PHYTO_QUANTITY_TYPE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "products[*].attributes.phytoQuantityType" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoQuantityType",
        },
        DFE: { path: "products[*].phytoQuantityType" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoQuantityType",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.REQUEST_PRODUCT_PHYTO_QUANTITY_TYPE)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Quantity",
        },
      },
      searchRepresentation: {
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.List),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "list_name",
            valueAttribute: "id",
            displayAttr: "list_name",
            orderBy: "list_name:asc",
            filter_by: "id := [unitOtherPhyto, unitWeight, unitVolume]",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_PHYTO_QUANTITY_VALUE,
    {
      fuid: Fuid.REQUEST_PRODUCT_PHYTO_QUANTITY_VALUE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoQuantityMeasure.value",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoQuantityMeasure.value",
        },
        DFE: { path: "products[*].phytoQuantityMeasureValue" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoQuantityMeasure.value",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.REQUEST_PRODUCT_PHYTO_QUANTITY_VALUE)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Quantity",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_PHYTO_QUANTITY_UNIT,
    {
      fuid: Fuid.REQUEST_PRODUCT_PHYTO_QUANTITY_UNIT,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoQuantityMeasure.unit",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoQuantityMeasure.unit",
        },
        DFE: { path: "products[*].phytoQuantityMeasureUnit" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoQuantityMeasure.unit",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.REQUEST_PRODUCT_PHYTO_QUANTITY_UNIT)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Quantity",
        },
      },
      searchRepresentation: {
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            dependOn: Fuid.REQUEST_PRODUCT_PHYTO_QUANTITY_TYPE,
            dependencyFilterKey: "list_code",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_PHYTO_VARIETY,
    {
      fuid: Fuid.REQUEST_PRODUCT_PHYTO_VARIETY,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoVariety",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoVariety",
        },
        DFE: { path: "products[*].phytoVariety" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoVariety",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].AdditionalProductInformation.Variety",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.REQUEST_PRODUCT_PHYTO_VARIETY)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/AdditionalProductInformation/Variety",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_PHYTO_APPROVED_SEED_CATEGORY,
    {
      fuid: Fuid.REQUEST_PRODUCT_PHYTO_APPROVED_SEED_CATEGORY,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoApprovedSeedCategory",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoApprovedSeedCategory",
        },
        DFE: { path: "products[*].phytoApprovedSeedCategory" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoApprovedSeedCategory",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].AdditionalProductInformation.ApprovedSeedCategory",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(
            Fuid.REQUEST_PRODUCT_PHYTO_APPROVED_SEED_CATEGORY,
          )}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/AdditionalProductInformation/ApprovedSeedCategory",
        },
      },
      searchRepresentation: {
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=ApprovedSeedCategory && active:=true`,
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.REQUEST_PRODUCT_PHYTO_LABEL_DESCRIPTION,
    {
      fuid: Fuid.REQUEST_PRODUCT_PHYTO_LABEL_DESCRIPTION,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoLableDescription",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoLableDescription",
        },
        DFE: { path: "products[*].phytoLabelDescription" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoLableDescription",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].AdditionalProductInformation.LabelDescription",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.REQUEST_PRODUCT_PHYTO_LABEL_DESCRIPTION)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/AdditionalProductInformation/LabelDescription",
        },
      },
      searchRepresentation: {
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Item),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "item_code,item_name",
            valueAttribute: "item_code",
            displayAttr: "item_name",
            orderBy: "item_name:asc",
            filter_by: `list_code:=LabelDescription && active:=true`,
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_GROUP_NUMBER,
    {
      fuid: Fuid.PRODUCT_GROUP_NUMBER,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "products[*].tradeLineGroupNumber" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].tradeLineGroupNumber",
        },
        DFE: { path: `products[*].productGroupNumber` },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].tradeLineGroupNumber",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_GROUP_NUMBER)}`,
        },
      },
    },
  ],
  [
    Fuid.TRANSFER_PRODUCT_SUPPORTING_DOC_ID,
    {
      fuid: Fuid.TRANSFER_PRODUCT_SUPPORTING_DOC_ID,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].supportingDocuments[*].supportingDocumentId",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].supportingDocuments[*].id",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].supportingDocuments[*].supportingDocumentId",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].supportingDocuments[*].id",
        },
        DFE: { path: "products[*].supportingDocuments[*].id" },
      },
    },
  ],
  [
    Fuid.TRANSFER_PRODUCT_SUPPORTING_DOC_IS_INHERITED,
    {
      fuid: Fuid.TRANSFER_PRODUCT_SUPPORTING_DOC_IS_INHERITED,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].supportingDocuments[*].isInherited",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].supportingDocuments[*].isInherited",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].supportingDocuments[*].isInherited",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].supportingDocuments[*].isInherited",
        },
        DFE: {
          AP_TRANSFER: {
            path: "products[*].supportingDocuments[*].isInherited",
          },
          AP_EXPORT: {
            path: "products[*].supportingDocuments[*].isInherited",
          },
        },
      },
    },
  ],
  [
    Fuid.DOC_COUNTRY_OF_ORIGIN,
    {
      fuid: Fuid.DOC_COUNTRY_OF_ORIGIN,
      serviceRepresentations: {
        DFE: { path: "docCountryOfOrigin" },
        ITEXT: { path: "SupportingDocumentComments" },
      },
    },
  ],
  [
    Fuid.DOC_DOCUMENT_REFERENCE_NUMBER,
    {
      fuid: Fuid.DOC_DOCUMENT_REFERENCE_NUMBER,
      serviceRepresentations: {
        DFE: { path: "docDocumentReferenceNumber" },
      },
    },
  ],
  [
    Fuid.DOC_DOCUMENT_TYPE,
    {
      fuid: Fuid.DOC_DOCUMENT_TYPE,
      serviceRepresentations: {
        DFE: { path: "docDocumentType" },
      },
    },
  ],
  [
    Fuid.DOC_FOREIGN_PREMISES_IDENTIFIER,
    {
      fuid: Fuid.DOC_FOREIGN_PREMISES_IDENTIFIER,
      serviceRepresentations: {
        DFE: { path: "docForeignPremisesIdentifier" },
      },
    },
  ],
  [
    Fuid.DOC_FOREIGN_PREMISES_NAME,
    {
      fuid: Fuid.DOC_FOREIGN_PREMISES_NAME,
      serviceRepresentations: {
        DFE: { path: "docForeignPremisesName" },
      },
    },
  ],
  [
    Fuid.DOC_IDENTIFICATION_MARKS,
    {
      fuid: Fuid.DOC_IDENTIFICATION_MARKS,
      serviceRepresentations: {
        DFE: { path: "docIdentificationMarks" },
      },
    },
  ],
  [
    Fuid.DOC_INTENDED_OPERATORS,
    {
      fuid: Fuid.DOC_INTENDED_OPERATORS,
      serviceRepresentations: {
        DFE: { path: "docIntendedOperators" },
      },
    },
  ],
  [
    Fuid.DOC_NZ_PREMISES_IDENTIFIER,
    {
      fuid: Fuid.DOC_NZ_PREMISES_IDENTIFIER,
      serviceRepresentations: {
        DFE: { path: "docNzPremisesIdentifier" },
      },
    },
  ],
  [
    Fuid.DOC_PRIMARY_OPERATOR,
    {
      fuid: Fuid.DOC_PRIMARY_OPERATOR,
      serviceRepresentations: {
        DFE: { path: "docPrimaryOperator" },
      },
    },
  ],
  [
    Fuid.DOC_PRODUCTION_DATES_FROM,
    {
      fuid: Fuid.DOC_PRODUCTION_DATES_FROM,
      serviceRepresentations: {
        DFE: { path: "docProductionDatesFrom" },
      },
    },
  ],
  [
    Fuid.DOC_PRODUCTION_DATES_TO,
    {
      fuid: Fuid.DOC_PRODUCTION_DATES_TO,
      serviceRepresentations: {
        DFE: { path: "docProductionDatesTo" },
      },
    },
  ],
  [
    Fuid.DOC_REASON_FOR_UPDATE,
    {
      fuid: Fuid.DOC_REASON_FOR_UPDATE,
      serviceRepresentations: {
        DFE: { path: "docReasonForUpdate" },
      },
    },
  ],
  [
    Fuid.DOC_REFERENCE_NUMBER,
    {
      fuid: Fuid.DOC_REFERENCE_NUMBER,
      serviceRepresentations: {
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].supportingDocuments[*].documentNumber",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].supportingDocuments[*].supportingDocumentNumber",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].supportingDocuments[*].documentNumber",
        },
        CORE_PHYTO_INPUT: {
          path: "supportingDocuments[*].documentReferenceNumber",
        },
        CORE_PHYTO_RESPONSE: {
          path: "supportingDocuments[*].documentReferenceNumber",
        },
        DFE: {
          PHYTO_EXPORT: { path: "supportingDocuments[*].id" },
          AP_EXPORT: {
            path: "products[*].supportingDocuments[*].documentNumber",
          },
          AP_TRANSFER: {
            path: "products[*].supportingDocuments[*].documentNumber",
          },
        },
        ITEXT: { path: "SupportingDocumentID" },
        G2G_IPPC_PHYTO_XML: {
          path: "supportingDocuments[*].documentReferenceNumber",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].supportingDocuments[*].documentNumber",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "supportingDocuments[*].documentReferenceNumber",
        },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].supportingDocuments[*].documentReferenceNumber",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].supportingDocuments[*].documentReferenceNumber",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/SupportingDocumentReferences/SupportingDocumentReference/SupportingDocumentID",
        },
      },
    },
  ],
  [
    Fuid.NSI_STATIONERY_SN_UPDATED_AT,
    {
      fuid: Fuid.NSI_STATIONERY_SN_UPDATED_AT,
      serviceRepresentations: {
        DFE: { path: "nsiStationerySNUpdatedAt" },
        ITEXT: { path: "StationerySerialNumberUpdatedDate" },
      },
    },
  ],
  [
    Fuid.NSI_STATIONERY_SERIAL_NUMBER,
    {
      fuid: Fuid.NSI_STATIONERY_SERIAL_NUMBER,
      serviceRepresentations: {
        DFE: { path: "nsiStationerySerialNumber" },
        ITEXT: { path: "StationerySerialNumber" },
      },
    },
  ],
  [
    Fuid.NSI_TOTAL_PACKAGES,
    {
      fuid: Fuid.NSI_TOTAL_PACKAGES,
      serviceRepresentations: {
        DFE: { path: "nsiTotalPackages" },
        ITEXT: { path: "TotalPackages" },
      },
    },
  ],
  [
    Fuid.PARTY_CERTIFYING_AGENCY_IDENTIFIER,
    {
      fuid: Fuid.PARTY_CERTIFYING_AGENCY_IDENTIFIER,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: { path: "certifyingAgencyId" },
        CORE_EXPORT_RESPONSE: { path: "certifyingAgencyId.id" },
        DFE: { path: "partyCertifyingAgencyIdentifier" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Parties/ThirdParty/IDandType",
        },
      },
      searchRepresentation: {
        AP_EXPORT: {
          collectionName: collectionAlias(CollectionName.Agency),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "display_name,agency_type,identifier",
            valueAttribute: "id",
            displayAttr: "display_name",
            orderBy: "display_name:asc",
          },
        },
        AP_TRANSFER: {
          collectionName: collectionAlias(CollectionName.Agency),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "display_name,agency_type,identifier",
            valueAttribute: "id",
            displayAttr: "display_name",
            orderBy: "display_name:asc",
          },
        },
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Agency),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "display_name,agency_type,identifier",
            valueAttribute: "id",
            displayAttr: "display_name",
            orderBy: "display_name:asc",
          },
        },
      },
      coreRepresentation: {
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PARTY_CONSIGNOR_ADDRESS_LINES,
    {
      fuid: Fuid.PARTY_CONSIGNOR_ADDRESS_LINES,
      serviceRepresentations: {
        DFE: { path: "partyConsignorAddressLines" },
        G2G_UNCEFACT_XML: { path: "consignor.addresses.lines" },
        ITEXT: { path: "ExporterAddress" },
      },
    },
  ],
  [
    Fuid.PARTY_CONSIGNOR_CITY,
    {
      fuid: Fuid.PARTY_CONSIGNOR_CITY,
      serviceRepresentations: {
        DFE: { path: "partyConsignorCity" },
        G2G_UNCEFACT_XML: { path: "consignor.addresses.city" },
        ITEXT: { path: "ExporterCity" },
      },
    },
  ],
  [
    Fuid.PARTY_CONSIGNOR_COUNTRY,
    {
      fuid: Fuid.PARTY_CONSIGNOR_COUNTRY,
      serviceRepresentations: {
        DFE: { path: "partyConsignorCountry" },
        G2G_UNCEFACT_XML: { path: "consignor.addresses.countryCode" },
        ITEXT: { path: "ExporterCountry" },
      },
    },
  ],
  [
    Fuid.PARTY_CONSIGNOR_NAME,
    {
      fuid: Fuid.PARTY_CONSIGNOR_NAME,
      serviceRepresentations: {
        DFE: { path: "partyConsignorName" },
        G2G_UNCEFACT_XML: { path: "consignor.name" },
        ITEXT: { path: "ExporterName" },
      },
    },
  ],
  [
    Fuid.PARTY_CONSIGNOR_POST_CODE,
    {
      fuid: Fuid.PARTY_CONSIGNOR_POST_CODE,
      serviceRepresentations: {
        DFE: { path: "partyConsignorPostCode" },
        G2G_UNCEFACT_XML: { path: "consignor.addresses.postCode" },
        ITEXT: { path: "ExporterPostCode" },
      },
    },
  ],
  [
    Fuid.PARTY_CONSIGNOR_STATE,
    {
      fuid: Fuid.PARTY_CONSIGNOR_STATE,
      serviceRepresentations: {
        DFE: { path: "partyConsignorState" },
        G2G_UNCEFACT_XML: { path: "consignor.addresses.state" },
        ITEXT: { path: "ExporterState" },
      },
    },
  ],
  [
    Fuid.PARTY_TD_CONSIGNEE_CITY,
    {
      fuid: Fuid.PARTY_TD_CONSIGNEE_CITY,
      serviceRepresentations: {
        DFE: { path: "partyTdConsigneeCity" },
        ITEXT: { path: "ConsigneeCity" },
      },
    },
  ],
  [
    Fuid.PARTY_TD_CONSIGNEE_NAME,
    {
      fuid: Fuid.PARTY_TD_CONSIGNEE_NAME,
      serviceRepresentations: {
        DFE: { path: "partyTdConsigneeName" },
        ITEXT: { path: "ConsigneeName" },
      },
    },
  ],
  [
    Fuid.PARTY_TD_CONSIGNOR_CITY,
    {
      fuid: Fuid.PARTY_TD_CONSIGNOR_CITY,
      serviceRepresentations: {
        DFE: { path: "partyTdConsignorCity" },
        ITEXT: { path: "ConsignorCity" },
      },
    },
  ],
  [
    Fuid.PARTY_TD_CONSIGNOR_NAME,
    {
      fuid: Fuid.PARTY_TD_CONSIGNOR_NAME,
      serviceRepresentations: {
        DFE: { path: "partyTdConsignorName" },
        ITEXT: { path: "ConsignorName" },
      },
    },
  ],
  [
    Fuid.PRODUCT_EXHAUSTED,
    {
      fuid: Fuid.PRODUCT_EXHAUSTED,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: {
          path: "products[*].exhausted",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].exhausted",
        },
        DFE: { path: "products[*].exhausted" },
        ITEXT: { path: "ItemExhausted" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].exhausted",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].exhausted",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].exhausted",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Exhausted",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_NET_UNITS_UNIT,
    {
      fuid: Fuid.PRODUCT_NET_UNITS_UNIT,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "products[*].attributes.otherQuantity.unit" },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.otherQuantity.unit",
        },
        DFE: { path: "products[*].netUnitsUnit" },
        G2G_IPPC_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${UNIT_LIST_CODE}')].attributes.phytoQuantityMeasure.unit`,
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.netUnits.unit",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${UNIT_LIST_CODE}')].attributes.phytoQuantityMeasure.unit`,
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Quantity.UnitType.unitCode",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_NET_UNITS_UNIT)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/NetUnits/@unitCode",
        },
      },
      searchRepresentation: {
        PHYTO_EXPORT: {
          collectionName: collectionAlias(CollectionName.Unit),
          usePermissions: false,
          collectionSettings: {
            queryAttributes: "unit_code,unit_name",
            valueAttribute: "unit_code",
            displayAttr: "unit_name",
            orderBy: "unit_name:asc",
            dependOn: Fuid.REQUEST_PRODUCT_PHYTO_QUANTITY_TYPE,
            dependencyFilterKey: "list_code",
          },
        },
      },
      coreRepresentation: {
        AP_EXPORT: true,
        AP_TRANSFER: true,
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_NET_UNITS_VALUE,
    {
      fuid: Fuid.PRODUCT_NET_UNITS_VALUE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.otherQuantity.value",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.otherQuantity.value",
        },
        DFE: { path: "products[*].netUnitsValue" },
        G2G_IPPC_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${UNIT_LIST_CODE}')].attributes.phytoQuantityMeasure.value`,
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.netUnits.value",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: `productItemGroupDetails.products[?(@.attributes?.phytoQuantityType?.id === '${UNIT_LIST_CODE}')].attributes.phytoQuantityMeasure.value`,
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].ProductItems[*].Quantity.UnitType.Value",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_NET_UNITS_VALUE)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/NetUnits",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_OVERSEAS_CERT_IDS,
    {
      fuid: Fuid.PRODUCT_OVERSEAS_CERT_IDS,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.overseasCertIDs",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.overseasCertIDs",
        },
        DFE: { path: "products[*].overseasCertIDs" },
        G2G_IPPC_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.overseasCertIDs",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.overseasCertIDs",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.overseasCertIDs",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].CertificationStatements.OverseasPhytosanitaryCertIDs[*]",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_OVERSEAS_CERT_IDS)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/CertificationStatements/OverseasPhytosanitaryCertIDs/OverseasPhytosanitaryCertID",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_IS_INSPECTION_ON_ORIGINAL,
    {
      fuid: Fuid.PRODUCT_PHYTO_IS_INSPECTION_ON_ORIGINAL,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoIsInspectionOnOriginal",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoIsInspectionOnOriginal",
        },
        DFE: { path: "products[*].phytoIsInspectionOnOriginal" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoIsInspectionOnOriginal",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].CertificationStatements.IsInspectionOnOriginal",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_IS_INSPECTION_ON_ORIGINAL)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/CertificationStatements/IsInspectionOnOriginal",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_SEED_LOT_REFERENCE,
    {
      fuid: Fuid.PRODUCT_PHYTO_SEED_LOT_REFERENCE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoSeedLotReference",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoSeedLotReference",
        },
        DFE: { path: "products[*].phytoSeedLotReference" },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_SEED_LOT_REFERENCE)}`,
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_PERCENTAGE_BY_WEIGHT,
    {
      fuid: Fuid.PRODUCT_PHYTO_PERCENTAGE_BY_WEIGHT,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoPercentageByWeight",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoPercentageByWeight",
        },
        DFE: { path: "products[*].phytoPercentageByWeight" },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_PERCENTAGE_BY_WEIGHT)}`,
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_SEED_LOT_APPROVAL,
    {
      fuid: Fuid.PRODUCT_PHYTO_SEED_LOT_APPROVAL,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoSeedLotApproval",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoSeedLotApproval",
        },
        DFE: { path: "products[*].phytoSeedLotApproval" },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_SEED_LOT_APPROVAL)}`,
        },
      },
      coreRepresentation: {
        PHYTO_EXPORT: true,
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_MIXTURE_NAME,
    {
      fuid: Fuid.PRODUCT_PHYTO_MIXTURE_NAME,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoMixtureName",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoMixtureName",
        },
        DFE: { path: "products[*].phytoMixtureName" },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_MIXTURE_NAME)}`,
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_IS_ORIGINAL_CERT,
    {
      fuid: Fuid.PRODUCT_PHYTO_IS_ORIGINAL_CERT,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoIsOriginalCert",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoIsOriginalCert",
        },
        DFE: { path: "products[*].phytoIsOriginalCert" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoIsOriginalCert",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].CertificationStatements.IsOriginal",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_IS_ORIGINAL_CERT)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/CertificationStatements/IsOriginal",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_IS_ORIGINAL_CONTAINER,
    {
      fuid: Fuid.PRODUCT_PHYTO_IS_ORIGINAL_CONTAINER,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoIsOriginalContainer",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoIsOriginalContainer",
        },
        DFE: { path: "products[*].phytoIsOriginalContainer" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoIsOriginalContainer",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].CertificationStatements.IsOriginalContainer",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_IS_ORIGINAL_CONTAINER)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/CertificationStatements/IsOriginalContainer",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_IS_REPACKED,
    {
      fuid: Fuid.PRODUCT_PHYTO_IS_REPACKED,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoIsRepacked",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoIsRepacked",
        },
        DFE: { path: "products[*].phytoIsRepacked" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoIsRepacked",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "Products[*].CertificationStatements.IsRepacked",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_IS_REPACKED)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/CertificationStatements/IsRepacked",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PHYTO_PACKHOUSE,
    {
      fuid: Fuid.PRODUCT_PHYTO_PACKHOUSE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].attributes.phytoPackhouse",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.phytoPackhouse",
        },
        DFE: { path: "products[*].phytoPackhouse" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].attributes.phytoPackhouse",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PHYTO_PACKHOUSE)}`,
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/Products/Product/ProductItems/ProductItem/Packhouse",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PRODUCT_DECLARATION,
    {
      fuid: Fuid.PRODUCT_PRODUCT_DECLARATION,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "products[*].productDeclaration",
        },
        CORE_PHYTO_RESPONSE: {
          path: "productItemGroupDetails.products[*].productDeclaration",
        },
        DFE: { path: "products[*].productDeclaration" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "productItemGroupDetails.products[*].productDeclaration",
        },
        PHYTO_PDF: {
          path: `productGroup.products[*].${toPhytoPdfPath(Fuid.PRODUCT_PRODUCT_DECLARATION)}`,
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "products[*].productDeclaration",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.PRODUCT_PRODUCT_NUMBER,
    {
      fuid: Fuid.PRODUCT_PRODUCT_NUMBER,
      serviceRepresentations: {
        DFE: { path: "products[*].productNumber" },
      },
    },
  ],
  [
    Fuid.PRODUCT_REQUEST_NUMBER,
    {
      fuid: Fuid.PRODUCT_REQUEST_NUMBER,
      serviceRepresentations: {
        DFE: { path: "products[*].requestNumber" },
      },
    },
  ],
  [
    Fuid.PRODUCT_TEMPERATURE,
    {
      fuid: Fuid.PRODUCT_TEMPERATURE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "products[*].attributes.temperature" },
        CORE_TRANSFER_RESPONSE: { path: "products[*].attributes.temperature" },
        CORE_EXPORT_INPUT: { path: "products[*].attributes.temperature" },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].attributes.temperature",
        },
        DFE: { path: "products[*].temperature" },
        ITEXT: { path: "TEMP" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].attributes.temperature",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].attributes.temperature",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].attributes.temperature",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_PHYTO_LOT_REFERENCE_NUMBER,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_PHYTO_LOT_REFERENCE_NUMBER,
      serviceRepresentations: {
        DFE: { path: "reqAttrPhytoLotReferenceNumber" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Remarks/Remark",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_ATTRIBUTE_STATEMENT_OF_RELABELLING,
    {
      fuid: Fuid.REQUEST_ATTRIBUTE_STATEMENT_OF_RELABELLING,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "attributes.phytoStatementOfRelabelling" },
        CORE_PHYTO_RESPONSE: { path: "attributes.phytoStatementOfRelabelling" },
        DFE: { path: "reqAttrStatementOfRelabelling" },
        B2G_SUBMISSION_PHYTO_XML: { path: "attributes.statementOfRelabelling" },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.REQUEST_ATTRIBUTE_STATEMENT_OF_RELABELLING),
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/StatementofRelabelling",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_CERTIFICATE_NUMBER,
    {
      fuid: Fuid.REQUEST_CERTIFICATE_NUMBER,
      serviceRepresentations: {
        DFE: { path: "reqCertificateNumber" },
        CORE_EXPORT_RESPONSE: { path: "certificateNumber" },
        CORE_PHYTO_RESPONSE: { path: "certificateNumber" },
        B2G_SUBMISSION_PHYTO_XML: { path: "certificateNumber" },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: { path: "Identifiers.CertificateID" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.REQUEST_CERTIFICATE_NUMBER) },
        ITEXT: { path: "ExternalIdentifier" },
        G2G_UNCEFACT_XML: { path: "certificateNumber" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "certificateNumber" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Identifiers/CertificateID",
        },
      },
    },
  ],
  [
    Fuid.REQUEST_CERTIFICATE_TYPE,
    {
      fuid: Fuid.REQUEST_CERTIFICATE_TYPE,
      serviceRepresentations: {
        DFE: { path: "reqCertificateType" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_APPROVED_AT,
    {
      fuid: Fuid.REQUEST_NSI_APPROVED_AT,
      serviceRepresentations: {
        DFE: { path: "reqNsiApprovedAt" },
        CORE_PHYTO_RESPONSE: { path: "approvedAt" },
        G2G_IPPC_PHYTO_XML: { path: "approvedAt" },
        G2G_UNCEFACT_XML: { path: "approvedAt" },
        B2G_SUBMISSION_PHYTO_XML: { path: "approvedAt" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.REQUEST_NSI_APPROVED_AT) },
        ITEXT: { path: "ApprovalDate" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_APPROVED_BY_CITY,
    {
      fuid: Fuid.REQUEST_NSI_APPROVED_BY_CITY,
      serviceRepresentations: {
        DFE: { path: "reqNsiApprovedByCity" },
        ITEXT: { path: "ApproverCity" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_APPROVED_BY_COUNTRY,
    {
      fuid: Fuid.REQUEST_NSI_APPROVED_BY_COUNTRY,
      serviceRepresentations: {
        DFE: { path: "reqNsiApprovedByCountry" },
        ITEXT: { path: "ApproverCountry" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_APPROVED_BY_NAME,
    {
      fuid: Fuid.REQUEST_NSI_APPROVED_BY_NAME,
      serviceRepresentations: {
        DFE: { path: "reqNsiApprovedByName" },
        ITEXT: { path: "ApprovedBy" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_APPROVED_BY_QUALIFICATIONS,
    {
      fuid: Fuid.REQUEST_NSI_APPROVED_BY_QUALIFICATIONS,
      serviceRepresentations: {
        DFE: { path: "reqNsiApprovedByQualifications" },
        ITEXT: { path: "ApproverQualifications" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_APPROVED_BY_TITLE,
    {
      fuid: Fuid.REQUEST_NSI_APPROVED_BY_TITLE,
      serviceRepresentations: {
        DFE: { path: "reqNsiApprovedByTitle" },
        ITEXT: { path: "ApproverTitle" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_AUDIT_COMMENTS,
    {
      fuid: Fuid.REQUEST_NSI_AUDIT_COMMENTS,
      serviceRepresentations: {
        DFE: { path: "reqNsiAuditComments" },
        ITEXT: { path: "AuditComment" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_AUDITED_AT,
    {
      fuid: Fuid.REQUEST_NSI_AUDITED_AT,
      serviceRepresentations: {
        DFE: { path: "reqNsiAuditedAt" },
        ITEXT: { path: "AuditedDate" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_AUDITED_BY_NAME,
    {
      fuid: Fuid.REQUEST_NSI_AUDITED_BY_NAME,
      serviceRepresentations: {
        DFE: { path: "reqNsiAuditedByName" },
        ITEXT: { path: "AuditedBy" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_LAST_MODIFIED_BY_NAME,
    {
      fuid: Fuid.REQUEST_NSI_LAST_MODIFIED_BY_NAME,
      serviceRepresentations: {
        DFE: { path: "reqNsiLastModifiedByName" },
        ITEXT: { path: "LastUpdatedBy" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_SUBMITTED_AT,
    {
      fuid: Fuid.REQUEST_NSI_SUBMITTED_AT,
      serviceRepresentations: {
        DFE: { path: "reqNsiSubmittedAt" },
        ITEXT: { path: "SubmissionDate" },
      },
    },
  ],
  [
    Fuid.REQUEST_NSI_SUBMITTED_BY_NAME,
    {
      fuid: Fuid.REQUEST_NSI_SUBMITTED_BY_NAME,
      serviceRepresentations: {
        DFE: { path: "reqNsiSubmittedByName" },
        ITEXT: { path: "SubmittedBy" },
      },
    },
  ],
  [
    Fuid.REQUEST_REPLACED_BY_CERTIFICATE_NUMBER,
    {
      fuid: Fuid.REQUEST_REPLACED_BY_CERTIFICATE_NUMBER,
      serviceRepresentations: {
        DFE: { path: "reqReplacedByCertificateNumber" },
      },
    },
  ],
  [
    Fuid.REQUEST_REPLACES_APPROVED_AT,
    {
      fuid: Fuid.REQUEST_REPLACES_APPROVED_AT,
      serviceRepresentations: {
        DFE: { path: "reqReplacesApprovedAt" },
        CORE_EXPORT_RESPONSE: { path: "replacesApprovedAt" },
        CORE_PHYTO_RESPONSE: { path: "replacesApprovedAt" },
        B2G_SUBMISSION_PHYTO_XML: { path: "replacesApprovedAt" },
        PHYTO_PDF: { path: toPhytoPdfPath(Fuid.REQUEST_REPLACES_APPROVED_AT) },
      },
    },
  ],
  [
    Fuid.REQUEST_REPLACES_CERTIFICATE_NUMBER,
    {
      fuid: Fuid.REQUEST_REPLACES_CERTIFICATE_NUMBER,
      serviceRepresentations: {
        DFE: { path: "reqReplacesCertificateNumber" },
        CORE_PHYTO_RESPONSE: { path: "certificateNumberReplacesId" },
        B2G_SUBMISSION_PHYTO_XML: { path: "certificateNumberReplacesId" },
        PHYTO_PDF: {
          path: toPhytoPdfPath(Fuid.REQUEST_REPLACES_CERTIFICATE_NUMBER),
        },
      },
    },
  ],
  [
    Fuid.REQUEST_SHIP_STORES,
    {
      fuid: Fuid.REQUEST_SHIP_STORES,
      serviceRepresentations: {
        CORE_EXPORT_INPUT: { path: "attributes.shipStores" },
        CORE_EXPORT_RESPONSE: { path: "attributes.shipStores" },
        G2G_UNCEFACT_XML: { path: "attributes.shipStores" },
        DFE: { path: "reqShipStores" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "attributes.shipStores" },
        SUBMISSION_PREVIEW_AP_XML_PATH: { path: "/Certificate/ShipStores" },
      },
    },
  ],
  [
    Fuid.REQUEST_UNIQUE_CONSIGNMENT_REFERENCE,
    {
      fuid: Fuid.REQUEST_UNIQUE_CONSIGNMENT_REFERENCE,
      serviceRepresentations: {
        CORE_TRANSFER_INPUT: { path: "uniqueConsignmentReference" },
        CORE_TRANSFER_RESPONSE: { path: "uniqueConsignmentReference" },
        CORE_EXPORT_INPUT: { path: "uniqueConsignmentReference" },
        CORE_EXPORT_RESPONSE: { path: "uniqueConsignmentReference" },
        DFE: { path: "reqUniqueConsignmentReference" },
        B2G: { path: "uniqueConsignmentReference" },
        B2G_SUBMISSION_AP_TRANSFER_XML: { path: "uniqueConsignmentReference" },
        G2G_UNCEFACT_XML: { path: "uniqueConsignmentReference" },
        B2G_SUBMISSION_AP_EXPORT_XML: { path: "uniqueConsignmentReference" },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Identifiers/UCRID",
        },
      },
    },
  ],
  [
    Fuid.TEMPLATE_SUBTITLE,
    {
      fuid: Fuid.TEMPLATE_SUBTITLE,
      serviceRepresentations: {
        DFE: { path: "templateSubtitle" },
        ITEXT: { path: "TemplateSubTitle" },
      },
    },
  ],
  [
    Fuid.TEMPLATE_TITLE,
    {
      fuid: Fuid.TEMPLATE_TITLE,
      serviceRepresentations: {
        DFE: { path: "templateTitle" },
        ITEXT: { path: "TemplateTitle" },
      },
    },
  ],
  [
    Fuid.DOC_COMMENTS,
    {
      fuid: Fuid.DOC_COMMENTS,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "supportingDocuments[*].comment" },
        CORE_PHYTO_RESPONSE: {
          path: "supportingDocuments[*].comment",
        },
        CORE_TRANSFER_INPUT: {
          path: "products[*].supportingDocuments[*].comment",
        },
        CORE_TRANSFER_RESPONSE: {
          path: "products[*].supportingDocuments[*].comment",
        },
        CORE_EXPORT_INPUT: {
          path: "products[*].supportingDocuments[*].comment",
        },
        CORE_EXPORT_RESPONSE: {
          path: "productItemGroupDetails.products[*].supportingDocuments[*].comment",
        },
        DFE: {
          PHYTO_EXPORT: { path: "supportingDocuments[*].docComments" },
          AP_EXPORT: { path: "products[*].supportingDocuments[*].docComments" },
          AP_TRANSFER: {
            path: "products[*].supportingDocuments[*].docComments",
          },
        },
        G2G_IPPC_PHYTO_XML: {
          path: "supportingDocuments[*].comment",
        },
        G2G_UNCEFACT_XML: {
          path: "productItemGroupDetails.products[*].supportingDocuments[*].comment",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "supportingDocuments[*].comment",
        },
        B2G_SUBMISSION_PHYTO_ERROR_FIELD: {
          path: "SupportingDocuments[*].Description",
        },
        ITEXT: { path: "SupportingDocumentComments" },
        B2G_SUBMISSION_AP_TRANSFER_XML: {
          path: "products[*].supportingDocuments[*].comment",
        },
        B2G_SUBMISSION_AP_EXPORT_XML: {
          path: "productItemGroupDetails.products[*].supportingDocuments[*].comment",
        },
        SUBMISSION_PREVIEW_AP_XML_PATH: {
          path: "/Certificate/Products/Product/SupportingDocumentReferences/SupportingDocumentReference/Comments",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/SupportingDocuments/Document/Description",
        },
      },
    },
  ],
  [
    Fuid.DOC_CONTENT_TYPE,
    {
      fuid: Fuid.DOC_CONTENT_TYPE,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: {
          path: "supportingDocuments[*].b2gFile.extensionType",
        },
        CORE_PHYTO_RESPONSE: {
          path: "supportingDocuments[*].b2gFile.extensionType",
        },
        DFE: { path: "supportingDocuments[*].docContentType" },
        G2G_IPPC_PHYTO_XML: {
          path: "supportingDocuments[*].b2gFile.extensionType",
        },
        G2G_UNCEFACT_XML: {
          path: "supportingDocuments[*].b2gFile.extensionType",
        },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "supportingDocuments[*].b2gFile.extensionType",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/SupportingDocuments/Document/ContentType",
        },
      },
    },
  ],
  [
    Fuid.DOC_FILE_NAME,
    {
      fuid: Fuid.DOC_FILE_NAME,
      serviceRepresentations: {
        CORE_PHYTO_INPUT: { path: "supportingDocuments[*].b2gFile.name" },
        CORE_PHYTO_RESPONSE: { path: "supportingDocuments[*].b2gFile.name" },
        DFE: { path: "supportingDocuments[*]._doc_fileName" },
        G2G_IPPC_PHYTO_XML: { path: "supportingDocuments[*].b2gFile.name" },
        G2G_UNCEFACT_XML: { path: "supportingDocuments[*].b2gFile.name" },
        B2G_SUBMISSION_PHYTO_XML: {
          path: "supportingDocuments[*].b2gFile.name",
        },
        SUBMISSION_PREVIEW_PHYTO_XML_PATH: {
          path: "/Certificate/SupportingDocuments/Document/FileName",
        },
      },
    },
  ],
])
